import {
  EmptyStateTitleText,
  EmptyStateSubtitleText,
} from "@louise/moon-ui/primitives/EmptyStateText";
import { useEffect, useState, type ReactNode } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
  type ImageSourcePropType,
} from "react-native";
import {
  Activity,
  Plus,
  Upload,
  ArrowRight,
  Beaker,
  Calendar,
  Calculator,
  ChevronRight,
  Clock,
  Dna,
  Droplets,
  FileText,
  FlaskConical,
  Gauge,
  Hash,
  Info,
  MapPin,
  Microscope,
  Shield,
  Snowflake,
  TestTube,
  Trash2,
  type LucideIcon,
  UploadCloud,
  UserRound,
  Scale,
  BadgeCheck,
  ListChecks,
} from "lucide-react-native";
import {
  ActionBanner,
  CardSubtitleText,
  CardTitleText,
  Dialog,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
  CompactActionButton,
  IconButton,
  MoonDetailRow,
  MoonSegmentedControl,
  SectionTitle,
  Tag,
  cardSurface,
  MetricValueText,
} from "@louise/moon-ui";
import { EMBER } from "../../onboarding/theme";
import {
  SpecimenFactCard as DetailCard,
  SpecimenFactTile as ValueGridTile,
} from "./SpecimenFactCard";
import {
  useLocale,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import { formatDate } from "@src/packages/i18n/formatters";
import type { Locale } from "@src/packages/i18n/locale";
import type {
  OocyteCohort,
  Specimen as ApiSpecimen,
  SpecimenUsage,
  StorageLocation,
} from "@src/packages/care/cryovault/useSpecimens";
import { readEmbryoStage } from "@src/packages/care/cryovault/embryoGrade";
import {
  describeEmbryoSex,
  readEmbryoSexChromosomes,
} from "@src/packages/care/cryovault/embryo-detail/embryoSex";
import { useEmbryoSexDisclosure } from "@src/packages/care/cryovault/embryo-detail/useEmbryoSexDisclosure";
import ShowEmbryoSexButton from "./ShowEmbryoSexButton";
import {
  formatEmbryoGradeFromDayDetails,
  getLatestEmbryoDevelopmentObservation,
  sortEmbryoDevelopmentObservations,
  type EmbryoDevelopmentObservation,
} from "./embryoDevelopment";
import {
  COUNTRIES,
  parseNotesString,
  parseStorageLocationMeta,
  STORAGE_LOCATION_TYPES,
} from "@src/packages/care/components/frozen-storage-data";
import { useDocumentUploadController } from "../DocumentUploadController";
import { PHYSICALLY_CONSUMED_SPECIMEN_USAGES } from "@src/packages/care/cryovault/frozenSpecimenUsages";
import {
  EditCryoSpecimenFieldDialog,
  type CryoSpecimenEditField,
} from "./EditCryoSpecimenFieldDialog";
import {
  CryoSpecimenLifecycleActionPicker,
  RecordCryoSpecimenLifecycleDialog,
  getLifecycleChoicesForSpecimen,
  type CryoSpecimenLifecycleChoice,
} from "./RecordCryoSpecimenLifecycleDialog";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/theme/tokens";
const SEMEN_SAMPLE_IMAGE =
  require("../../assets/images/3d/semen-sample.png") as ImageSourcePropType;
const OOCYTE_SAMPLE_IMAGE =
  require("../../assets/images/3d/oocyte-sample.png") as ImageSourcePropType;
const EMBRYO_SAMPLE_IMAGE =
  require("../../assets/images/embryo-microscope.jpg") as ImageSourcePropType;
const SPECIMEN_DELETE_ILLUSTRATION =
  require("../../assets/images/you-icons/trash-bin.png") as ImageSourcePropType;

export type CryoSpecimenType = "embryo" | "oocyte" | "sperm";
type GameteSource = "self" | "partner" | "donor";
export type CryoSpecimenUsageEvent = {
  id?: string | null;
  usage: string;
  date?: string | null;
  time?: string | null;
  cycleId?: string | null;
  notes?: string | null;
  hasDecisionAndConsent?: boolean | null;
  createdAt?: string | null;
  source?: string | null;
};
export type CryoSpecimenCycleRelationship = {
  cycleId: string;
  relation: "origin" | "used_in" | "transfer";
  usage?: string;
  date?: string | null;
  source?: string | null;
};
type SpecimenDetailTab =
  "summary" | "clinical" | "storage" | "usage" | "documents";

export type CryoSpecimenDetail = {
  id: string;
  type: CryoSpecimenType;
  label: string;
  facility: string;
  status?: string | null;
  storageResolutionStatus?: "resolved" | "needs_review" | null;
  collectedAt?: string | null;
  sampleId?: string | null;
  identificationNumber?: string | null;
  maturityStage?: string | null;
  batchId?: string | null;
  cycleId?: string | null;
  cycleLabel?: string | null;
  useCycleId?: string | null;
  useCycleLabel?: string | null;
  transferCycleId?: string | null;
  transferCycleLabel?: string | null;
  /**
   * Explicit structured cycle relationships. The scalar IDs above remain
   * compatibility projections for the current modal copy; new consumers
   * should use this list so multiple usage events are not collapsed.
   */
  cycleRelationships?: CryoSpecimenCycleRelationship[];
  /**
   * Set only on oocyte-cohort rows, real or synthesised, and never sent by
   * the API: the ids of the individual specimens the cohort stands for, so
   * consumers can reach the members a cohort row collapses.
   */
  cohortMemberSpecimenIds?: string[];
  frozenDate?: string | null;
  storageId?: string | null;
  storageLocationType?: string | null;
  storageVessel?: string | null;
  tankNumber?: string | null;
  canisterNumber?: string | null;
  caneNumber?: string | null;
  subcanisterNumber?: string | null;
  rackNumber?: string | null;
  strawColor?: string | null;
  strawNumber?: string | null;
  country?: string | null;
  storageCity?: string | null;
  storageCountryName?: string | null;
  notes?: string | null;
  rawNotes?: string | null;
  recordSource?: ApiSpecimen["source"] | null;
  ownership?: ApiSpecimen["ownership"] | null;
  storageLocations?: StorageLocation[];
  mediaUrls?: string[] | null;
  timelapseUrl?: string | null;
  source?: GameteSource | null;
  sourceEggId?: string | null;
  sourceEggSource?: GameteSource | null;
  sourceSpermId?: string | null;
  sourceSpermSource?: GameteSource | null;
  usageEvents?: CryoSpecimenUsageEvent[];
  /** Complete usage records used when appending a new lifecycle event. */
  specimenUsages?: SpecimenUsage[];
  oocyteRecordKind?: "individual" | "cohort" | null;
  embryoId?: string | null;
  embryoStatus?: string | null;
  embryoSex?: string | null;
  grade?: string | null;
  pgtResult?: "euploid" | "aneuploid" | "mosaic" | "untested" | null;
  pgtPerformed?: string | null;
  transferCandidate?: boolean;
  fertilizationDate?: string | null;
  fertilizationMethod?: "ICSI" | "conventional" | null;
  dayOfDevelopment?: string | null;
  pgtReportDate?: string | null;
  pgtReportSummary?: string | null;
  developmentObservations?: EmbryoDevelopmentObservation[] | null;
  retrievalDate?: string | null;
  freezingMethod?: string | null;
  totalRetrieved?: number | null;
  matureMII?: number | null;
  immatureMI?: number | null;
  degenerated?: number | null;
  numberFrozen?: number | null;
  numberThawed?: number | null;
  fertilizedCount?: number | null;
  postThawSurvivalRate?: number | null;
  predictiveReportName?: string | null;
  predictiveReportProvider?: string | null;
  predictiveReportDate?: string | null;
  predictedEggQuality?: string | null;
  predictedFertilityOutlook?: string | null;
  predictiveReportAssumptions?: string | null;
  processingMethod?: string | null;
  collectionMethod?: string | null;
  preparationMethod?: string | null;
  abstinencePeriod?: number | null;
  analysisDate?: string | null;
  otherSampleDetails?: string | null;
  volume?: string | null;
  totalCount?: string | null;
  tmsc?: string | null;
  motility?: string | null;
  progressiveMotility?: string | null;
  concentration?: string | null;
  morphology?: string | null;
  ph?: string | null;
  viscosity?: string | null;
  nonProgressiveMotility?: string | null;
  immotile?: string | null;
  agglutinationAggregation?: string | null;
  vitality?: string | null;
  leukocytes?: string | null;
  dnaFragmentationIndex?: string | null;
  antispermAntibody?: string | null;
  spermEpigenetics?: string | null;
  oxidativeStress?: string | null;
  spermFunction?: string | null;
};

type ApiStorageLocationWithDetails = ApiSpecimen["storageLocations"][number] & {
  storageVessel?: string | null;
  tankNumber?: string | null;
  canisterNumber?: string | null;
  caneNumber?: string | null;
  subcanisterNumber?: string | null;
  rackNumber?: string | null;
  strawColor?: string | null;
  strawNumber?: string | null;
};

type ApiSpecimenWithDetails = ApiSpecimen & {
  identificationNumber?: string | null;
  maturityStage?: string | null;
  grade?: string | null;
  origin?: string | null;
  processingMethod?: string | null;
  collectionMethod?: string | null;
  abstinencePeriod?: number | null;
  /** Specimen-level semen metrics; the API codes them as observations. */
  vitality?: number | null;
  dnaFragmentation?: number | null;
  sampleId?: string | null;
  oocyteSpecimenId?: string | null;
  spermiogram?: {
    notes?: string | null;
    semenVolume?: number | null;
    volume?: number | null;
    volumeUnit?: string | null;
    spermCount?: number | null;
    totalCount?: number | null;
    spermConcentration?: number | null;
    concentration?: number | null;
    motility?: number | null;
    totalMotility?: number | null;
    progressiveMotility?: number | string | null;
    nonProgressiveMotility?: number | string | null;
    immotile?: number | string | null;
    normalMorphology?: number | null;
    morphology?: number | null;
    tmsc?: number | null;
    pH?: number | null;
    viscosity?: number | null;
    agglutinationAggregation?: number | string | null;
    agglutination?: number | string | null;
    aggregation?: number | string | null;
    vitality?: number | string | null;
    leukocytes?: number | string | null;
    dnaFragmentationIndex?: number | string | null;
    dnaFragmentation?: number | string | null;
    antispermAntibody?: boolean | number | string | null;
    spermEpigenetics?: number | string | null;
    methylation?: number | string | null;
    oxidativeStress?: number | string | null;
    spermFunction?: number | string | null;
    spermQT?: number | string | null;
    date?: string | null;
    time?: string | null;
    preparationMethod?: string | null;
    origin?: string | null;
  } | null;
  specimenUsages?: {
    id?: string | null;
    specimenId?: string | null;
    usage?: string | null;
    cycleId?: string | null;
    date?: string | null;
    time?: string | null;
    notes?: string | null;
    hasDecisionAndConsent?: boolean | null;
    createdAt?: string | null;
    source?: string | null;
    freezingInfo?: {
      freezingProtocol?: string | null;
      freezingMedia?: string | null;
    } | null;
    thawingInfo?: {
      didSurvive?: boolean | null;
      postThawQualityMaturityAssessment?: string | null;
      motilityPostThaw?: number | null;
      viability?: number | null;
    } | null;
    embryoBiopsy?: {
      euploidyStatus?: string | null;
      hasMosaicism?: boolean | null;
      mosaicismLevel?: string | null;
      sentToPgta?: boolean | null;
      sentToPgtm?: boolean | null;
      sentToPgtsr?: boolean | null;
      biopsyStage?: string | null;
    } | null;
  }[];
};

const PGT_CONFIG: Record<string, { labelKey: string; color: string }> = {
  euploid: { labelKey: "cryo_vault.pgt.euploid", color: EMBER.colors.green },
  aneuploid: {
    labelKey: "cryo_vault.pgt.aneuploid",
    color: EMBER.colors.coral,
  },
  mosaic: { labelKey: "cryo_vault.pgt.mosaic", color: EMBER.colors.gold },
  untested: {
    labelKey: "cryo_vault.pgt.untested",
    color: EMBER.colors.textFaint,
  },
};

const GRADE_EXPLANATION_KEYS: Record<string, string> = {
  "6AA": "cryo_vault.grade_explanations.6AA",
  "5AA": "cryo_vault.grade_explanations.5AA",
  "4AA": "cryo_vault.grade_explanations.4AA",
  "4AB": "cryo_vault.grade_explanations.4AB",
  "4BA": "cryo_vault.grade_explanations.4BA",
  "4BB": "cryo_vault.grade_explanations.4BB",
  "3AA": "cryo_vault.grade_explanations.3AA",
  "3AB": "cryo_vault.grade_explanations.3AB",
  "3BA": "cryo_vault.grade_explanations.3BA",
  "3BB": "cryo_vault.grade_explanations.3BB",
  "2BB": "cryo_vault.grade_explanations.2BB",
  "1BB": "cryo_vault.grade_explanations.1BB",
};

const SOURCE_LABEL_KEYS: Record<
  GameteSource,
  Record<CryoSpecimenType, string>
> = {
  self: {
    oocyte: "cryo_vault.source_labels.self_egg",
    sperm: "cryo_vault.source_labels.self_sperm",
    embryo: "",
  },
  partner: {
    oocyte: "cryo_vault.source_labels.partner_egg",
    sperm: "cryo_vault.source_labels.partner_sperm",
    embryo: "",
  },
  donor: {
    oocyte: "cryo_vault.source_labels.donor_egg",
    sperm: "cryo_vault.source_labels.donor_sperm",
    embryo: "",
  },
};

export function buildCryoSpecimenLabel(
  t: TranslationFunction,
  apiType: string,
  index: number,
) {
  const template =
    apiType === "embryo"
      ? t("cryo_vault.labels.embryo_label")
      : apiType === "oocyte_cohort"
        ? t("cryo_vault.labels.egg_cohort_label")
        : apiType === "oocyte"
          ? t("cryo_vault.labels.egg_label")
          : t("cryo_vault.labels.sample_label");
  return template.replace("${n}", String(index));
}

/**
 * Server-side migration notes ("Migrated from fan-out specimens.notes …")
 * used to leak into the patient's own notes field. Filter them out at read
 * time; they carry no clinical meaning for her and read as noise. If a note
 * is *only* the migration line, return null so the "Your notes" tile shows
 * an empty state rather than blank quoted text.
 */
const MIGRATION_NOTE_PATTERN =
  /Migrated from [\w.-]+specimens?\.notes[^\n]*(?:source copies remain represented by observations\.?)?/gi;

function filterServerMetadataFromNotes(
  notes: string | null | undefined,
): string | null {
  if (!notes) return null;
  const stripped = notes.replace(MIGRATION_NOTE_PATTERN, "").trim();
  return stripped.length > 0 ? stripped : null;
}

function formatSpecimenDate(
  raw: string | null | undefined,
  locale: Locale,
): string | null {
  if (!raw) return null;
  const datePart = raw.split("T")[0];
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return formatDate(parsed, locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function titleCase(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bIvf\b/g, "IVF")
    .replace(/\bIcsi\b/g, "ICSI")
    .replace(/\bPgt\b/g, "PGT")
    .replace(/\bPGT A M\b/g, "PGT-A + PGT-M")
    .replace(/\bPGT A\b/g, "PGT-A")
    .replace(/\bPGT M\b/g, "PGT-M")
    .replace(/\bPGT Sr\b/g, "PGT-SR")
    .replace(/\bMii\b/g, "MII")
    .replace(/\bMi\b/g, "MI");
}

function formatEnumValue(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value === "xx" || value === "xy") return value.toUpperCase();
  if (value.length <= 3 && value === value.toUpperCase()) return value;
  return titleCase(value);
}

function formatOptionLabel(
  options: { id: string; label: string }[],
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return options.find((option) => option.id === value)?.label ?? value;
}

function normalizeGameteSource(
  value: string | null | undefined,
): GameteSource | null {
  if (!value) return null;
  const normalized = value.toLowerCase().replace(/[\s-]+/g, "_");
  if (
    normalized === "self" ||
    normalized === "mine" ||
    normalized === "patient" ||
    normalized === "own" ||
    normalized === "yourself"
  ) {
    return "self";
  }
  if (normalized === "partner" || normalized === "spouse") {
    return "partner";
  }
  if (normalized === "donor") {
    return "donor";
  }
  return null;
}

function formatNumber(value: number | null | undefined): string | null {
  return value == null ? null : String(value);
}

function formatMetricValue(
  value: boolean | number | string | null | undefined,
): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? "Positive" : "Negative";
  return String(value);
}

function formatPercentMetric(
  value: number | string | null | undefined,
): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "number") return `${value}%`;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return /%|normal|abnormal|positive|negative|detected|not detected/i.test(
    trimmed,
  )
    ? trimmed
    : `${trimmed}%`;
}

function compactParts(parts: (string | null | undefined)[]): string | null {
  const filtered = parts.filter((part): part is string => Boolean(part));
  return filtered.length > 0 ? filtered.join(" · ") : null;
}

function formatStorageLocator(specimen: CryoSpecimenDetail): string | null {
  const structuredLocator = compactParts([
    specimen.storageVessel ? formatEnumValue(specimen.storageVessel) : null,
    specimen.tankNumber ? `Tank ${specimen.tankNumber}` : null,
    specimen.canisterNumber ? `Canister ${specimen.canisterNumber}` : null,
    specimen.caneNumber ? `Cane ${specimen.caneNumber}` : null,
    specimen.subcanisterNumber
      ? `Subcanister ${specimen.subcanisterNumber}`
      : null,
    specimen.rackNumber ? `Rack ${specimen.rackNumber}` : null,
    specimen.strawColor
      ? `${formatEnumValue(specimen.strawColor)} straw`
      : null,
    specimen.strawNumber ? `Straw ${specimen.strawNumber}` : null,
  ]);
  return structuredLocator ?? specimen.storageId ?? null;
}

function getSpecimenTitle(
  t: TranslationFunction,
  specimen: CryoSpecimenDetail,
) {
  if (specimen.type === "oocyte" && specimen.oocyteRecordKind === "cohort") {
    const index = specimen.label.match(/\d+/)?.[0];
    return index
      ? t("cryo_vault.modal.egg_cohort_title", {
          params: { n: index },
        })
      : t("cryo_vault.modal.egg_cohort_title_without_number");
  }

  if (specimen.type !== "sperm") return specimen.label;
  const index = specimen.label.match(/\d+/)?.[0];
  return index
    ? t("cryo_vault.modal.semen_sample_title", {
        params: { n: index },
      })
    : t("cryo_vault.modal.semen_sample_title_without_number");
}

function getSpecimenSourceDocumentName(
  t: TranslationFunction,
  specimen: CryoSpecimenDetail,
) {
  if (specimen.type === "sperm") {
    return t("cryo_vault.modal.semen_analysis_report");
  }
  if (specimen.type === "embryo") {
    return t("cryo_vault.modal.embryology_report");
  }
  return t("cryo_vault.modal.retrieval_report");
}

const CYCLE_NUMBER_WORDS: Record<string, string> = {
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
};

function normalizeCycleNumber(value: string | undefined) {
  if (!value) return null;
  const normalized = value.toLowerCase();
  const numericValue = value.replace(/^0+(?=\d)/, "");
  return CYCLE_NUMBER_WORDS[normalized] ?? numericValue;
}

function getCompactCycleLabel(
  t: TranslationFunction,
  cycleLabel: string | null | undefined,
) {
  const rawLabel = cycleLabel?.trim();
  if (!rawLabel) return null;

  const labelWithoutClinic =
    rawLabel
      .split(/\s+[·–-]\s+/)
      .at(-1)
      ?.trim() ?? rawLabel;
  const searchableLabel = `${labelWithoutClinic} ${rawLabel}`;
  const numberMatch =
    searchableLabel.match(
      /(?:#|no\.?\s*|number\s+)(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\b/i,
    ) ??
    searchableLabel.match(
      /\bcycle\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\b/i,
    );
  const cycleNumber = normalizeCycleNumber(numberMatch?.[1]);

  if (cycleNumber) {
    return t("cryo_vault.modal.cycle_number_label", {
      params: { number: cycleNumber },
    });
  }
  return t("cryo_vault.modal.cycle_label_compact");
}

function getCycleLabelParts(cycleLabel: string | null | undefined) {
  return (
    cycleLabel
      ?.split(/\s+[·–-]\s+/)
      .map((part) => part.trim())
      .filter(Boolean) ?? []
  );
}

function getCycleMonthLabel(cycleLabel: string | null | undefined) {
  const match = cycleLabel?.match(
    /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+(?:\d{1,2},?\s+)?\d{4}\b/i,
  );
  return match?.[0] ?? null;
}

function getCycleHeaderTag(
  t: TranslationFunction,
  cycleLabel: string | null | undefined,
) {
  const compactCycleLabel =
    getCompactCycleLabel(t, cycleLabel) ??
    t("cryo_vault.modal.cycle_label_compact");
  const monthLabel = getCycleMonthLabel(cycleLabel);

  if (!monthLabel) {
    return t("cryo_vault.modal.origin_cycle_header_tag_compact", {
      params: { cycle: compactCycleLabel },
    });
  }
  return t("cryo_vault.modal.origin_cycle_header_tag", {
    params: { cycle: compactCycleLabel, date: monthLabel },
  });
}

function getCycleMetadataTags(cycleLabel: string | null | undefined) {
  const parts = getCycleLabelParts(cycleLabel);
  if (parts.length <= 1) return [];
  const clinicPart = parts[0];
  return clinicPart ? [clinicPart] : [];
}

type SpecimenHeaderImageConfig = {
  source: ImageSourcePropType;
  width: number;
  height: number;
  borderRadius?: number;
};

function getSpecimenHeaderImageConfig(
  type: CryoSpecimenType,
  compactLayout: boolean,
): SpecimenHeaderImageConfig {
  if (type === "sperm") {
    return {
      source: SEMEN_SAMPLE_IMAGE,
      width: compactLayout ? 132 : 172,
      height: compactLayout ? 58 : 78,
    };
  }

  if (type === "oocyte") {
    return {
      source: OOCYTE_SAMPLE_IMAGE,
      width: compactLayout ? 82 : 104,
      height: compactLayout ? 82 : 104,
    };
  }

  return {
    source: EMBRYO_SAMPLE_IMAGE,
    width: compactLayout ? 86 : 108,
    height: compactLayout ? 66 : 82,
    borderRadius: 18,
  };
}

type DetailItem = {
  key: string;
  label: string;
  value: string | null | undefined;
  icon: LucideIcon;
  explanation?: ClinicalMetricExplanation;
  /** Set only when the explicit cycle relationship resolves to a patient cycle. */
  onPress?: () => void;
};

type ClinicalMetricItem = {
  key: string;
  label: string;
  value: string | null | undefined;
  icon?: LucideIcon;
  placeholder?: string;
  explanation?: ClinicalMetricExplanation;
  children?: ReactNode;
  /** Set only when the explicit cycle relationship resolves to a patient cycle. */
  onPress?: () => void;
};

type SemenMetricInfoKey =
  | "analysis_date"
  | "collection_method"
  | "processing_method"
  | "preparation_method"
  | "abstinence"
  | "volume"
  | "concentration"
  | "total_count"
  | "tmsc"
  | "motility"
  | "progressive_motility"
  | "non_progressive_motility"
  | "immotile"
  | "morphology"
  | "agglutination_aggregation"
  | "ph"
  | "viscosity"
  | "vitality"
  | "leukocytes"
  | "dna_fragmentation_index"
  | "antisperm_antibody"
  | "sperm_epigenetics"
  | "oxidative_stress"
  | "sperm_function";

type OocyteMetricInfoKey =
  | "oocyte_maturity"
  | "oocyte_freezing_method"
  | "oocyte_retrieval_date"
  | "oocyte_frozen_date"
  | "oocyte_total_retrieved"
  | "oocyte_mature_mii"
  | "oocyte_number_frozen"
  | "oocyte_number_thawed"
  | "oocyte_remaining_frozen"
  | "oocyte_maturity_rate"
  | "oocyte_predictive_quality"
  | "oocyte_predictive_outlook";

type ClinicalMetricExplanation = {
  title: string;
  currentValue: string;
  referenceRange: string;
  statusLabel: string;
  statusBody: string;
  sourceLabel: string;
  sourceBody: string;
  summary: string;
  context: string;
  icon?: LucideIcon;
  disclaimer?: string;
};

function buildSemenMetricExplanation(
  t: TranslationFunction,
  key: SemenMetricInfoKey,
  title: string,
  value: string | null | undefined,
  placeholder: string,
  icon?: LucideIcon,
): ClinicalMetricExplanation {
  const hasValue = value != null && value !== "";
  return {
    title,
    currentValue: value ?? placeholder,
    referenceRange: t(`cryo_vault.metric_info.${key}.range`),
    statusLabel: hasValue
      ? t("cryo_vault.metric_info.status_needs_source_range_title")
      : t("cryo_vault.metric_info.status_not_tested_title"),
    statusBody: hasValue
      ? t("cryo_vault.metric_info.status_needs_source_range_body")
      : t("cryo_vault.metric_info.status_not_tested_body"),
    sourceLabel: t("cryo_vault.metric_info.source_not_linked_title"),
    sourceBody: t("cryo_vault.metric_info.source_not_linked_body"),
    summary: t(`cryo_vault.metric_info.${key}.summary`),
    context: t(`cryo_vault.metric_info.${key}.context`),
    icon,
  };
}

function buildOocyteMetricExplanation(
  t: TranslationFunction,
  key: OocyteMetricInfoKey,
  title: string,
  value: string | null | undefined,
  placeholder: string,
  icon?: LucideIcon,
  sourceLinked = false,
): ClinicalMetricExplanation {
  const hasValue = value != null && value !== "";
  return {
    title,
    currentValue: value ?? placeholder,
    referenceRange: t(`cryo_vault.metric_info.${key}.range`),
    statusLabel: hasValue
      ? t("cryo_vault.metric_info.oocyte_status_needs_source_title")
      : t("cryo_vault.metric_info.status_not_tested_title"),
    statusBody: hasValue
      ? t("cryo_vault.metric_info.oocyte_status_needs_source_body")
      : t("cryo_vault.metric_info.status_not_tested_body"),
    sourceLabel: sourceLinked
      ? t("cryo_vault.metric_info.source_report_linked_title")
      : t("cryo_vault.metric_info.source_not_linked_title"),
    sourceBody: sourceLinked
      ? t("cryo_vault.metric_info.source_report_linked_body")
      : t("cryo_vault.metric_info.source_not_linked_body"),
    summary: t(`cryo_vault.metric_info.${key}.summary`),
    context: t(`cryo_vault.metric_info.${key}.context`),
    disclaimer: t("cryo_vault.metric_info.oocyte_disclaimer"),
    icon,
  };
}

function DetailSection({
  title,
  rows,
}: {
  title?: string | null;
  rows: DetailItem[];
}) {
  const capturePress = usePressCapture("SpecimenDetailSection");
  const visibleRows = rows.filter((row) => Boolean(row.value));
  if (visibleRows.length === 0) return null;

  return (
    <View style={{ gap: 10, marginTop: 16 }}>
      {title && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "700",
            color: EMBER.colors.textFaint,
            letterSpacing: 0.3,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Text>
      )}
      <View style={{ gap: 10 }}>
        {visibleRows.map((row) =>
          row.onPress ? (
            <Pressable
              key={row.key}
              onPress={() => {
                capturePress(pressLabel(row.label), "specimen_detail_row");
                row.onPress?.();
              }}
              accessibilityRole="button"
              accessibilityLabel={`${row.label}: ${row.value}`}
              style={({ pressed }) => ({ opacity: pressed ? 0.72 : 1 })}
            >
              <MoonDetailRow
                icon={row.icon}
                label={row.label}
                value={row.value!}
                trailing={
                  <ChevronRight
                    size={14}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.8}
                  />
                }
              />
            </Pressable>
          ) : (
            <MoonDetailRow
              key={row.key}
              icon={row.icon}
              label={row.label}
              value={row.value!}
            />
          ),
        )}
      </View>
    </View>
  );
}

function SpecimenModalHeader({
  image,
  title,
  tags,
  metadataTags,
  compactLayout,
}: {
  image: SpecimenHeaderImageConfig;
  title: string;
  tags: string[];
  metadataTags: string[];
  compactLayout: boolean;
}) {
  return (
    <View>
      <View
        style={{
          alignItems: "center",
          marginTop: compactLayout ? -8 : -4,
          marginBottom: compactLayout ? 8 : 12,
        }}
      >
        <Image
          source={image.source}
          resizeMode="contain"
          accessibilityIgnoresInvertColors
          style={{
            width: image.width,
            height: image.height,
            borderRadius: image.borderRadius,
          }}
        />
      </View>
      <View style={{ alignItems: "center" }}>
        <DialogHeader align="center" title={title} />
        {tags.length > 0 && (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
              marginTop: 8,
              justifyContent: "center",
            }}
          >
            {tags.map((tag) => (
              <Tag key={tag} label={tag} shape="pill" />
            ))}
          </View>
        )}
        {metadataTags.length > 0 && (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
              marginTop: 10,
              justifyContent: "center",
            }}
          >
            {metadataTags.map((tag) => (
              <Tag key={tag} label={tag} variant="outline" shape="pill" />
            ))}
          </View>
        )}
      </View>
    </View>
  );
}

function EmptyDetailCard({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <View style={{ gap: 4, marginTop: 16 }}>
      <View style={{ gap: 4 }}>
        <EmptyStateTitleText>{title}</EmptyStateTitleText>
        <EmptyStateSubtitleText style={{ marginTop: 0 }}>
          {body}
        </EmptyStateSubtitleText>
      </View>
      {action ? <View style={{ marginTop: 8 }}>{action}</View> : null}
    </View>
  );
}

function ValueGridCard({
  title,
  rows,
  onMetricPress,
  children,
}: {
  title: string;
  rows: ClinicalMetricItem[];
  onMetricPress?: (row: ClinicalMetricItem) => void;
  children?: ReactNode;
}) {
  const visibleRows = rows.filter(
    (row) =>
      Boolean(row.value) || Boolean(row.placeholder) || Boolean(row.children),
  );
  if (visibleRows.length === 0) return null;

  return (
    <View style={{ gap: 12, marginTop: 16 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "700",
          color: EMBER.colors.textFaint,
          letterSpacing: 0.3,
          textTransform: "uppercase",
        }}
      >
        {title}
      </Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {visibleRows.map((row) => (
          <ValueGridTile
            key={row.key}
            label={row.label}
            value={row.value ?? row.placeholder}
            icon={row.icon}
            muted={!row.value && Boolean(row.placeholder)}
            onPress={
              row.onPress ??
              (row.explanation && onMetricPress
                ? () => onMetricPress(row)
                : undefined)
            }
          >
            {row.children}
          </ValueGridTile>
        ))}
      </View>
      {children}
    </View>
  );
}

function ValueGridTagGroup({ labels }: { labels: string[] }) {
  if (labels.length === 0) return null;

  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
      {labels.map((label) => (
        <Tag key={label} label={label} shape="pill" />
      ))}
    </View>
  );
}

function ClinicalMetricCard({
  title,
  rows,
  onMetricPress,
}: {
  title: string;
  rows: ClinicalMetricItem[];
  onMetricPress?: (row: ClinicalMetricItem) => void;
}) {
  return (
    <ValueGridCard title={title} rows={rows} onMetricPress={onMetricPress} />
  );
}

function ClinicalMetricExplanationDialog({
  explanation,
  onClose,
}: {
  explanation: ClinicalMetricExplanation | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  if (!explanation) return null;
  const MetricIcon = explanation.icon ?? Info;

  return (
    <Dialog
      visible
      onClose={onClose}
      closeButton="minimal"
      contentStyle={{
        paddingTop: 30,
        paddingHorizontal: 22,
        paddingBottom: 22,
      }}
    >
      <DialogHeader
        leading={
          <MetricIcon
            size={22}
            color={EMBER.colors.textMid}
            strokeWidth={1.6}
          />
        }
        title={explanation.title}
      />

      {/* The dialog stops at 90% of the screen; the explanation scrolls
          under the title rather than being cut off. */}
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 4 }}
      >
        <View style={{ flexDirection: "row", gap: 8, marginTop: 18 }}>
          <MetricInfoBox
            icon={Gauge}
            label={t("cryo_vault.metric_info.current_value")}
            value={
              <MetricValueText size="compact">
                {explanation.currentValue}
              </MetricValueText>
            }
          />
          <MetricInfoBox
            icon={Scale}
            label={t("cryo_vault.metric_info.reference_context")}
            value={
              <CardSubtitleText style={{ marginTop: 0 }}>
                {explanation.referenceRange}
              </CardSubtitleText>
            }
          />
        </View>

        <MetricInfoTextBlock
          title={t("cryo_vault.metric_info.what_it_means")}
          body={explanation.summary}
        />
        <MetricInfoTextBlock
          title={t("cryo_vault.metric_info.keep_in_mind")}
          body={explanation.context}
        />
        <View style={{ marginTop: 14, gap: 10 }}>
          <MetricContextLine
            icon={BadgeCheck}
            label={t("cryo_vault.metric_info.result_status")}
            value={explanation.statusLabel}
            body={explanation.statusBody}
          />
          <View
            style={{
              height: 1,
              backgroundColor: semanticColors.border.subtle,
            }}
          />
          <MetricContextLine
            icon={FileText}
            label={t("cryo_vault.metric_info.result_source")}
            value={explanation.sourceLabel}
            body={explanation.sourceBody}
          />
        </View>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: EMBER.colors.textFaint,
            marginTop: 12,
            textAlign: "center",
          }}
        >
          {explanation.disclaimer ?? t("cryo_vault.metric_info.disclaimer")}
        </Text>
      </ScrollView>
    </Dialog>
  );
}

function MetricContextLine({
  icon,
  label,
  value,
  body,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  body: string;
}) {
  return (
    <MoonDetailRow
      icon={icon}
      label={label}
      value={
        <View style={{ gap: 2 }}>
          <CardTitleText>{value}</CardTitleText>
          <CardSubtitleText style={{ marginTop: 0 }}>{body}</CardSubtitleText>
        </View>
      }
    />
  );
}

function MetricInfoBox({
  icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
}) {
  return (
    <View
      style={{ ...cardSurface({ variant: "panel" }), flex: 1, padding: 10 }}
    >
      <MoonDetailRow icon={icon} label={label} value={value} />
    </View>
  );
}

function MetricInfoTextBlock({ title, body }: { title: string; body: string }) {
  return (
    <View style={{ marginTop: 16, gap: 5 }}>
      <SectionTitle level="subsection">{title}</SectionTitle>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 19,
          color: EMBER.colors.textMid,
        }}
      >
        {body}
      </Text>
    </View>
  );
}

function DocumentRequirementList({
  title,
  items,
  statusLabel,
  actionLabel,
  onActionPress,
}: {
  title: string;
  items: { key: string; title: string; body: string }[];
  statusLabel: string;
  actionLabel: string;
  onActionPress: (item: { key: string; title: string; body: string }) => void;
}) {
  return (
    <View style={{ gap: 10, marginTop: 16 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "700",
          color: EMBER.colors.textFaint,
          letterSpacing: 0.3,
          textTransform: "uppercase",
        }}
      >
        {title}
      </Text>
      {items.map((item) => (
        <View
          key={item.key}
          style={{
            ...cardSurface({ variant: "panel" }),
            padding: 12,
            gap: 8,
          }}
        >
          <View style={{ flexDirection: "row", gap: 10 }}>
            <FileText
              size={16}
              color={EMBER.colors.text}
              strokeWidth={1.8}
              style={{ marginTop: 1 }}
            />
            <View style={{ flex: 1 }}>
              <CardTitleText>{item.title}</CardTitleText>
              <CardSubtitleText>{item.body}</CardSubtitleText>
            </View>
          </View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Tag label={statusLabel} shape="pill" />
            <CompactActionButton
              label={actionLabel}
              accessibilityLabel={`${actionLabel}: ${item.title}`}
              action="document_requirement"
              onPress={() => onActionPress(item)}
              variant="secondary"
              size="sm"
              leadingIcon={UploadCloud}
              iconStrokeWidth={1.8}
              numberOfLines={1}
            />
          </View>
        </View>
      ))}
    </View>
  );
}

function ProvenanceRow({
  specimenType,
  resolved,
  fallbackSource,
  canNavigate,
  onPress,
}: {
  specimenType: CryoSpecimenType;
  resolved: CryoSpecimenDetail | null;
  fallbackSource?: GameteSource | null;
  canNavigate: boolean;
  onPress?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("SpecimenProvenanceRow");
  const source = resolved?.source ?? fallbackSource;
  const sourceLabel = source
    ? t(SOURCE_LABEL_KEYS[source][specimenType])
    : specimenType === "oocyte"
      ? t("cryo_vault.source_labels.default_egg")
      : t("cryo_vault.source_labels.default_sperm");
  const label = resolved?.label ?? sourceLabel;
  const subtitle =
    resolved && sourceLabel
      ? compactParts([sourceLabel, resolved.frozenDate])
      : (resolved?.frozenDate ??
        (specimenType === "oocyte"
          ? t("cryo_vault.source_labels.subtitle_used_for_fertilization")
          : null));

  const content = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <View style={{ flex: 1, gap: 2 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: EMBER.colors.text,
          }}
        >
          {label}
        </Text>
        {subtitle && (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 11, color: EMBER.colors.textFaint }}
          >
            {subtitle}
          </Text>
        )}
      </View>
      {canNavigate && (
        <ChevronRight
          size={14}
          color={EMBER.colors.textFaint}
          strokeWidth={1.5}
        />
      )}
    </View>
  );

  if (canNavigate && onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={() => {
          capturePress(pressLabel(label), "specimen_provenance_row");
          onPress();
        }}
      >
        {content}
      </Pressable>
    );
  }

  return content;
}

function SpecimenLinkRow({
  specimen,
  subtitle,
  tags = [],
  onPress,
}: {
  specimen: CryoSpecimenDetail;
  subtitle?: string | null;
  tags?: string[];
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("SpecimenLinkRow");
  const content = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 8,
      }}
    >
      <View style={{ flex: 1, gap: 4 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "700",
            color: EMBER.colors.text,
          }}
        >
          {specimen.label}
        </Text>
        {subtitle && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 15,
              color: EMBER.colors.textFaint,
            }}
          >
            {subtitle}
          </Text>
        )}
        {tags.length > 0 && <ValueGridTagGroup labels={tags} />}
      </View>
      {onPress && (
        <ChevronRight
          size={14}
          color={EMBER.colors.textFaint}
          strokeWidth={1.5}
        />
      )}
    </View>
  );
  if (!onPress) return content;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={specimen.label}
      onPress={() => {
        capturePress(pressLabel(specimen.label), "specimen_link_row");
        onPress();
      }}
      style={({ pressed }) => ({ opacity: pressed ? 0.72 : 1 })}
    >
      {content}
    </Pressable>
  );
}

export function mapApiSpecimensToCryoDetails(
  apiSpecimens: ApiSpecimen[],
  labelBuilder: (apiType: string, index: number) => string,
): CryoSpecimenDetail[] {
  const counters: Record<string, number> = {};

  return apiSpecimens.map((specimen) => {
    const detailedSpecimen = specimen as ApiSpecimenWithDetails;
    const localType = specimen.type === "semen" ? "sperm" : specimen.type;
    counters[localType] = (counters[localType] ?? 0) + 1;
    const label = labelBuilder(specimen.type, counters[localType]!);

    const activeStorage = specimen.storageLocations.find(
      (location) => location.isActive && location.storageType === "frozen",
    ) as ApiStorageLocationWithDetails | undefined;
    const storageMeta = parseStorageLocationMeta(activeStorage?.notes);
    const storageResolutionStatus = activeStorage?.siteResolutionStatus ?? null;

    const parsed = specimen.notes ? parseNotesString(specimen.notes) : null;

    const mapPgtResult = (
      geneticStatus?: string,
    ): CryoSpecimenDetail["pgtResult"] => {
      if (!geneticStatus) return null;
      const lower = geneticStatus.toLowerCase();
      if (lower === "euploid" || lower === "normal") return "euploid";
      if (lower === "aneuploid" || lower === "abnormal") return "aneuploid";
      if (lower === "mosaic") return "mosaic";
      if (["not_tested", "untested", "not_performed"].includes(lower)) {
        return "untested";
      }
      // A failed or pending result is not the same as testing never being
      // performed. Leave it unset until the UI has a dedicated no-result
      // projection backed by the report model.
      return null;
    };

    const mapFertMethod = (
      method?: string,
    ): CryoSpecimenDetail["fertilizationMethod"] => {
      if (!method) return null;
      if (method === "icsi") return "ICSI";
      if (method === "conventional" || method === "ivf") {
        return "conventional";
      }
      return null;
    };

    const embryoIndex = counters[localType]! - 1;
    const individualEmbryo = parsed?.individualEmbryos?.[embryoIndex] ?? null;
    const freezeUsage = detailedSpecimen.specimenUsages?.find(
      (usage) => usage.usage === "freeze",
    );
    const thawUsage = detailedSpecimen.specimenUsages?.find(
      (usage) => usage.usage === "thaw",
    );
    const usageEvents = (detailedSpecimen.specimenUsages ?? [])
      .filter((usage): usage is NonNullable<typeof usage> & { usage: string } =>
        Boolean(usage.usage),
      )
      .map((usage) => ({
        id: usage.id,
        usage: usage.usage,
        date: usage.date,
        time: usage.time,
        cycleId: usage.cycleId,
        notes: usage.notes,
        hasDecisionAndConsent: usage.hasDecisionAndConsent,
        createdAt: usage.createdAt,
        source: usage.source,
      }));
    const cycleRelationships: CryoSpecimenCycleRelationship[] = specimen.cycleId
      ? [{ cycleId: specimen.cycleId, relation: "origin" }]
      : [];
    for (const usage of usageEvents) {
      if (!usage.cycleId) continue;
      if (usage.usage === "transfer") {
        cycleRelationships.push({
          cycleId: usage.cycleId,
          relation: "transfer",
          usage: usage.usage,
          date: usage.date,
          source: usage.source,
        });
      } else if (["thaw", "fertilize", "use"].includes(usage.usage)) {
        cycleRelationships.push({
          cycleId: usage.cycleId,
          relation: "used_in",
          usage: usage.usage,
          date: usage.date,
          source: usage.source,
        });
      }
    }
    const consumingRelationship = cycleRelationships.find(
      (relationship) => relationship.relation === "used_in",
    );
    const embryoBiopsy = detailedSpecimen.specimenUsages?.find(
      (usage) => usage.embryoBiopsy,
    )?.embryoBiopsy;
    const pgtFromBiopsy =
      embryoBiopsy?.sentToPgta || embryoBiopsy?.sentToPgtm
        ? embryoBiopsy.sentToPgta && embryoBiopsy.sentToPgtm
          ? "PGT-A + PGT-M"
          : embryoBiopsy.sentToPgta
            ? "PGT-A"
            : "PGT-M"
        : embryoBiopsy?.sentToPgtsr
          ? "PGT-SR"
          : null;
    const biopsyStage = embryoBiopsy?.biopsyStage?.replace("day_", "");
    const pgtResultFromBiopsy = embryoBiopsy?.hasMosaicism
      ? "mosaic"
      : mapPgtResult(embryoBiopsy?.euploidyStatus ?? undefined);
    const spermiogram = detailedSpecimen.spermiogram;
    const semenVolumeValue = spermiogram?.semenVolume ?? spermiogram?.volume;
    const semenVolume =
      semenVolumeValue != null
        ? `${semenVolumeValue}${spermiogram?.volumeUnit ? ` ${spermiogram.volumeUnit}` : " mL"}`
        : null;
    const spermTotalCount = spermiogram?.spermCount ?? spermiogram?.totalCount;
    const spermConcentration =
      spermiogram?.spermConcentration ?? spermiogram?.concentration;
    const spermMotility = spermiogram?.motility ?? spermiogram?.totalMotility;
    const spermProgressiveMotility = spermiogram?.progressiveMotility;
    const spermAgglutinationAggregation =
      formatMetricValue(spermiogram?.agglutinationAggregation) ??
      compactParts([
        spermiogram?.agglutination != null
          ? `Agglutination ${spermiogram.agglutination}`
          : null,
        spermiogram?.aggregation != null
          ? `Aggregation ${spermiogram.aggregation}`
          : null,
      ]);
    const developmentObservations = sortEmbryoDevelopmentObservations(
      detailedSpecimen.dayDetails,
    );
    const latestDevelopmentObservation = getLatestEmbryoDevelopmentObservation(
      detailedSpecimen.dayDetails,
    );
    const explicitGameteSource =
      normalizeGameteSource(detailedSpecimen.origin) ??
      normalizeGameteSource(spermiogram?.origin) ??
      normalizeGameteSource(parsed?.specimenSource);
    const gameteSource =
      detailedSpecimen.isDonor || explicitGameteSource === "donor"
        ? "donor"
        : explicitGameteSource;

    const projectedStatus = parsed?.currentStatus ?? specimen.status;
    const status =
      ["stored", "processed"].includes(projectedStatus) && !activeStorage
        ? "storage_pending"
        : projectedStatus === "stored" &&
            storageResolutionStatus === "needs_review" &&
            parsed?.storageState !== "stored"
          ? "storage_unverified"
          : projectedStatus;

    return {
      id: specimen.id,
      type: localType as CryoSpecimenType,
      label,
      facility: activeStorage?.facility ?? "",
      status,
      storageResolutionStatus,
      collectedAt: specimen.collectedAt,
      sampleId:
        localType === "sperm" ? (detailedSpecimen.sampleId ?? null) : null,
      identificationNumber:
        localType === "oocyte"
          ? (detailedSpecimen.identificationNumber ?? null)
          : null,
      maturityStage:
        localType === "oocyte"
          ? (detailedSpecimen.maturityStage ?? null)
          : null,
      cycleId: specimen.cycleId,
      useCycleId: consumingRelationship?.cycleId ?? null,
      transferCycleId:
        cycleRelationships.find(
          (relationship) => relationship.relation === "transfer",
        )?.cycleId ?? null,
      cycleRelationships,
      frozenDate: activeStorage?.storedFrom ?? null,
      storageId: storageMeta?.storageId ?? parsed?.storageId ?? null,
      storageLocationType:
        storageMeta?.facilityType ?? parsed?.storageLocationType ?? null,
      storageVessel: activeStorage?.storageVessel ?? null,
      tankNumber: activeStorage?.tankNumber ?? null,
      canisterNumber: activeStorage?.canisterNumber ?? null,
      caneNumber: activeStorage?.caneNumber ?? null,
      subcanisterNumber: activeStorage?.subcanisterNumber ?? null,
      rackNumber: activeStorage?.rackNumber ?? null,
      strawColor: activeStorage?.strawColor ?? null,
      strawNumber: activeStorage?.strawNumber ?? null,
      country: storageMeta?.country ?? parsed?.country ?? null,
      storageCity: storageMeta?.location ?? parsed?.storageCity ?? null,
      storageCountryName: storageMeta?.countryName ?? null,
      notes: filterServerMetadataFromNotes(parsed?.freeTextNotes ?? null),
      rawNotes: specimen.notes,
      recordSource: specimen.source,
      ownership: specimen.ownership,
      storageLocations: specimen.storageLocations,
      embryoId:
        individualEmbryo?.embryoId ?? detailedSpecimen.identificationNumber,
      embryoStatus:
        status === "storage_pending" || status === "storage_unverified"
          ? status
          : (individualEmbryo?.status ?? status ?? null),
      embryoSex: individualEmbryo?.sex ?? null,
      grade:
        localType === "embryo"
          ? (individualEmbryo?.grade ??
            detailedSpecimen.grade ??
            formatEmbryoGradeFromDayDetails(detailedSpecimen.dayDetails))
          : localType === "oocyte"
            ? detailedSpecimen.maturityStage
            : null,
      pgtResult: mapPgtResult(
        individualEmbryo?.geneticStatus ??
          parsed?.pgtPerformed ??
          pgtResultFromBiopsy ??
          undefined,
      ),
      pgtPerformed: parsed?.pgtPerformed ?? pgtFromBiopsy,
      developmentObservations:
        localType === "embryo" && developmentObservations.length > 0
          ? developmentObservations
          : null,
      fertilizationDate: parsed?.fertilisationDate ?? null,
      fertilizationMethod: mapFertMethod(parsed?.fertilisationMethod),
      dayOfDevelopment:
        parsed?.dayOfDevelopment ??
        (latestDevelopmentObservation?.day != null
          ? String(latestDevelopmentObservation.day)
          : biopsyStage) ??
        null,
      cycleLabel: parsed?.clinicName
        ? `${parsed.clinicName}${parsed.freezeDate ? ` · ${parsed.freezeDate}` : ""}`
        : null,
      retrievalDate: parsed?.retrievalDate ?? null,
      freezingMethod:
        parsed?.freezingMethod ??
        freezeUsage?.freezingInfo?.freezingProtocol ??
        null,
      totalRetrieved: parsed?.totalRetrieved ?? null,
      matureMII: parsed?.matureMII ?? null,
      immatureMI: parsed?.immatureMI ?? null,
      degenerated: parsed?.degenerated ?? null,
      numberFrozen: parsed?.numberFrozen ?? null,
      numberThawed: parsed?.numberThawed ?? null,
      postThawSurvivalRate:
        parsed?.postThawSurvivalRate ??
        thawUsage?.thawingInfo?.viability ??
        null,
      processingMethod: detailedSpecimen.processingMethod ?? null,
      collectionMethod: detailedSpecimen.collectionMethod ?? null,
      preparationMethod: spermiogram?.preparationMethod ?? null,
      abstinencePeriod: detailedSpecimen.abstinencePeriod ?? null,
      analysisDate: spermiogram?.date ?? null,
      otherSampleDetails:
        spermiogram?.notes?.trim() || parsed?.otherDetails?.trim() || null,
      volume: semenVolume,
      totalCount: spermTotalCount != null ? String(spermTotalCount) : null,
      tmsc: spermiogram?.tmsc != null ? String(spermiogram.tmsc) : null,
      motility: spermMotility != null ? `${spermMotility}%` : null,
      progressiveMotility:
        spermProgressiveMotility != null
          ? `${spermProgressiveMotility}%`
          : null,
      concentration:
        spermConcentration != null ? `${spermConcentration} M/mL` : null,
      morphology:
        spermiogram?.normalMorphology != null
          ? `${spermiogram.normalMorphology}% normal`
          : spermiogram?.morphology != null
            ? `${spermiogram.morphology}%`
            : null,
      ph: spermiogram?.pH != null ? String(spermiogram.pH) : null,
      viscosity:
        spermiogram?.viscosity != null ? String(spermiogram.viscosity) : null,
      nonProgressiveMotility: formatPercentMetric(
        spermiogram?.nonProgressiveMotility,
      ),
      immotile: formatPercentMetric(spermiogram?.immotile),
      agglutinationAggregation: spermAgglutinationAggregation,
      // Specimen-level first: that is where the intake writes them and where
      // the API returns them. The spermiogram keys stay as a fallback for
      // clinic-imported analyses that carry them inline.
      vitality: formatPercentMetric(
        detailedSpecimen.vitality ?? spermiogram?.vitality,
      ),
      leukocytes: formatMetricValue(spermiogram?.leukocytes),
      dnaFragmentationIndex: formatPercentMetric(
        detailedSpecimen.dnaFragmentation ??
          spermiogram?.dnaFragmentationIndex ??
          spermiogram?.dnaFragmentation,
      ),
      antispermAntibody: formatMetricValue(spermiogram?.antispermAntibody),
      spermEpigenetics: formatMetricValue(
        spermiogram?.spermEpigenetics ?? spermiogram?.methylation,
      ),
      oxidativeStress: formatMetricValue(spermiogram?.oxidativeStress),
      spermFunction: formatMetricValue(
        spermiogram?.spermFunction ?? spermiogram?.spermQT,
      ),
      source:
        specimen.type === "oocyte" || specimen.type === "semen"
          ? gameteSource
          : null,
      sourceEggId:
        specimen.type === "embryo"
          ? (detailedSpecimen.oocyteSpecimenId ?? null)
          : null,
      usageEvents,
      specimenUsages: specimen.specimenUsages,
      oocyteRecordKind: specimen.type === "oocyte" ? "individual" : null,
    };
  });
}

export function mapApiOocyteCohortsToCryoDetails(
  apiCohorts: OocyteCohort[],
): CryoSpecimenDetail[] {
  return apiCohorts.map((cohort) => {
    const firstCollection = cohort.members
      .map((member) => member.collectedAt)
      .filter(Boolean)
      .sort()[0];
    const activeMember = cohort.members.find(
      (member) => member.storageFacility || member.frozenDate,
    );
    // Historical cohorts sometimes leave `storageFacility` unset on their
    // members. Fall back to the cohort label's trailing segment after "·"
    // (e.g. `Historical retrieval · CCRM Fertility` → `CCRM Fertility`), so
    // `hasFacilityDetails` upstream still picks a facility name and the
    // tiles render inside a facility card instead of a flat grid.
    const cohortFacilityFromLabel =
      cohort.label?.split("·").pop()?.trim() ?? "";
    const cohortFacility =
      activeMember?.storageFacility ??
      (cohortFacilityFromLabel.length > 0 ? cohortFacilityFromLabel : "");
    const cohortFrozenDate =
      cohort.members
        .map((member) => member.frozenDate)
        .filter((date): date is string => Boolean(date))
        .sort()[0] ?? null;
    // Populate storageLocations so the detail modal's Storage tab has
    // something to render for the cohort — before this it was empty, since
    // the read path expects `storageLocations` and cohort records only
    // carried per-member `storageFacility` + `frozenDate`.
    const cohortStorageLocations =
      cohortFacility || cohortFrozenDate
        ? [
            {
              isActive: true,
              storageType: "frozen" as const,
              facility: cohortFacility,
              storedFrom: cohortFrozenDate ?? "",
              storedTo: null,
              notes: null,
            },
          ]
        : [];

    return {
      id: cohort.id,
      type: "oocyte",
      label: cohort.label,
      facility: cohortFacility,
      status: cohort.status,
      collectedAt: firstCollection ?? cohort.retrievalDate ?? null,
      batchId: cohort.id,
      cycleId: cohort.originCycleId,
      cycleRelationships: cohort.originCycleId
        ? [{ cycleId: cohort.originCycleId, relation: "origin" as const }]
        : [],
      cohortMemberSpecimenIds: cohort.members.map((member) => member.id),
      frozenDate: cohortFrozenDate,
      storageLocations: cohortStorageLocations,
      notes: cohort.notes,
      rawNotes: cohort.notes,
      recordSource: cohort.source,
      ownership: cohort.ownership,
      retrievalDate: cohort.retrievalDate,
      totalRetrieved: cohort.totalRetrieved,
      matureMII: cohort.matureMII,
      immatureMI: cohort.immatureMI,
      degenerated: cohort.degenerated,
      numberFrozen: cohort.numberFrozen ?? cohort.members.length,
      numberThawed: cohort.numberThawed,
      fertilizedCount: cohort.fertilizedCount,
      oocyteRecordKind: "cohort",
      source: null,
    };
  });
}

export function CryoSpecimenDetailModal({
  specimen,
  allSpecimens,
  visible,
  onClose,
  onPlanTreatment,
  onSelectSpecimen,
  onDelete,
  getCycleNavigation,
  getEmbryoReportNavigation,
}: {
  specimen: CryoSpecimenDetail | null;
  allSpecimens: CryoSpecimenDetail[];
  visible: boolean;
  onClose: () => void;
  onPlanTreatment?: (specimen: CryoSpecimenDetail) => void;
  onSelectSpecimen?: (specimenId: string) => void;
  onDelete?: (specimen: CryoSpecimenDetail) => Promise<void>;
  /**
   * Resolve an explicit origin/usage/transfer cycle relationship to a
   * navigation action. Unresolved IDs intentionally remain display-only.
   */
  getCycleNavigation?: (cycleId: string) => (() => void) | undefined;
  /** Resolve an embryo's source cycle to its authorized testing report. */
  getEmbryoReportNavigation?: (cycleId: string) => (() => void) | undefined;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CryoSpecimenDetailModal");
  const { locale } = useLocale();
  const { height: windowHeight } = useWindowDimensions();
  const { openDocumentUpload } = useDocumentUploadController();
  const [activeTab, setActiveTab] = useState<SpecimenDetailTab>("summary");
  const [selectedMetricExplanation, setSelectedMetricExplanation] =
    useState<ClinicalMetricExplanation | null>(null);
  const [editingField, setEditingField] =
    useState<CryoSpecimenEditField | null>(null);
  const [lifecycleActionPickerVisible, setLifecycleActionPickerVisible] =
    useState(false);
  const [lifecycleChoice, setLifecycleChoice] =
    useState<CryoSpecimenLifecycleChoice | null>(null);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const sexDisclosure = useEmbryoSexDisclosure(specimen?.id);
  useEffect(() => {
    setActiveTab("summary");
    setSelectedMetricExplanation(null);
    setEditingField(null);
    setLifecycleActionPickerVisible(false);
    setLifecycleChoice(null);
    setDeleteConfirmationVisible(false);
    setDeleteError(null);
  }, [specimen?.id, visible]);
  if (!visible || !specimen) return null;
  const isClinicControlledRecord =
    specimen.recordSource === "provider" || specimen.ownership === "provider";
  const canEditUserRecord = !isClinicControlledRecord;
  const canDeleteUserRecord =
    canEditUserRecord && specimen.oocyteRecordKind !== "cohort";
  const canEditField = (field: CryoSpecimenEditField) =>
    canEditUserRecord &&
    (specimen.oocyteRecordKind !== "cohort" || field === "notes");
  const editableValue = (
    value: string | null | undefined,
    field: CryoSpecimenEditField,
  ) =>
    value || (canEditField(field) ? t("cryo_vault.modal.not_recorded") : null);
  const dialogMaxHeight = Math.max(360, windowHeight - 48);
  const compactLayout = windowHeight < 760;
  const headerImage = getSpecimenHeaderImageConfig(
    specimen.type,
    compactLayout,
  );
  const reservedDialogHeight = compactLayout ? 340 : 390;
  const modalMaxHeight = Math.min(
    compactLayout ? 340 : 460,
    Math.max(160, dialogMaxHeight - reservedDialogHeight),
  );

  const pgt = specimen.pgtResult ? PGT_CONFIG[specimen.pgtResult] : null;
  const gradeExplanationKey = specimen.grade
    ? GRADE_EXPLANATION_KEYS[specimen.grade]
    : null;
  const gradeExplanation = gradeExplanationKey ? t(gradeExplanationKey) : null;
  const sourceEgg = specimen.sourceEggId
    ? allSpecimens.find((item) => item.id === specimen.sourceEggId)
    : null;
  const sourceSperm = specimen.sourceSpermId
    ? allSpecimens.find((item) => item.id === specimen.sourceSpermId)
    : null;
  const originCycleId =
    specimen.cycleRelationships?.find(
      (relationship) => relationship.relation === "origin",
    )?.cycleId ?? specimen.cycleId;
  const usedInCycleId =
    specimen.cycleRelationships?.find(
      (relationship) => relationship.relation === "used_in",
    )?.cycleId ?? specimen.useCycleId;
  const transferCycleId =
    specimen.cycleRelationships?.find(
      (relationship) => relationship.relation === "transfer",
    )?.cycleId ?? specimen.transferCycleId;
  const embryosCreatedFromOocyte =
    specimen.type === "oocyte"
      ? allSpecimens.filter(
          (item) => item.type === "embryo" && item.sourceEggId === specimen.id,
        )
      : [];

  const frozenDateFormatted = formatSpecimenDate(specimen.frozenDate, locale);
  const fertilizationDateFormatted = formatSpecimenDate(
    specimen.fertilizationDate,
    locale,
  );
  const pgtReportDateFormatted = formatSpecimenDate(
    specimen.pgtReportDate,
    locale,
  );
  const collectedDateFormatted = formatSpecimenDate(
    specimen.collectedAt,
    locale,
  );
  const analysisDateFormatted = formatSpecimenDate(
    specimen.analysisDate,
    locale,
  );
  const retrievalDateFormatted = formatSpecimenDate(
    specimen.retrievalDate,
    locale,
  );
  const predictiveReportDateFormatted = formatSpecimenDate(
    specimen.predictiveReportDate,
    locale,
  );
  const isOocyteCohort =
    specimen.type === "oocyte" && specimen.oocyteRecordKind === "cohort";
  const specimenTitle = getSpecimenTitle(t, specimen);
  const sourceDocumentName = getSpecimenSourceDocumentName(t, specimen);
  // The chip reads the specimen record. A `use` sets it to "used" on the
  // backend. A thaw is recorded as a usage event but leaves the status at
  // "stored", so a stored specimen with a recorded thaw reads "Thawed" rather
  // than "Stored" while the vault lists it in history. Cohorts keep their own
  // count-based status.
  const hasRecordedThaw =
    !isOocyteCohort &&
    specimen.status === "stored" &&
    (specimen.specimenUsages ?? []).some((usage) => usage.usage === "thaw");
  const rawStatusValue = specimen.embryoStatus ?? specimen.status;
  const rawStatusLabel =
    rawStatusValue === "storage_pending"
      ? t("cryo_vault.modal.storage_pending")
      : rawStatusValue === "storage_unverified"
        ? t("cryo_vault.modal.storage_unverified")
        : rawStatusValue === "used"
          ? t("cryo_vault.modal.status_value_used")
          : hasRecordedThaw
            ? t("cryo_vault.modal.status_value_thawed")
            : formatEnumValue(rawStatusValue);
  const statusLabel =
    isOocyteCohort && specimen.status === "partially_used"
      ? t("cryo_vault.modal.cohort_partially_used")
      : rawStatusLabel;
  const storageLocator = formatStorageLocator(specimen);
  const storageContext = compactParts([
    specimen.storageCity,
    specimen.storageLocationType
      ? formatOptionLabel(STORAGE_LOCATION_TYPES, specimen.storageLocationType)
      : null,
    specimen.storageCountryName ??
      formatOptionLabel(COUNTRIES, specimen.country),
  ]);
  const notTestedLabel = t("cryo_vault.modal.not_tested");
  const embryoSexChromosomes = readEmbryoSexChromosomes(specimen.embryoSex);
  const embryoSex = embryoSexChromosomes
    ? describeEmbryoSex(embryoSexChromosomes, t)
    : null;
  const embryoSexLabel =
    embryoSex && !sexDisclosure.hidden
      ? embryoSex.label
      : t("care.embryo_sex.sex");
  // "Unknown" and "Prefer not to know" record no sex, but say more than
  // "Not tested" would.
  const embryoSexPlaceholder =
    specimen.embryoSex === "unknown" || specimen.embryoSex === "prefer_not"
      ? t(`cryo_vault.add_modal.options.sex.${specimen.embryoSex}`)
      : notTestedLabel;
  const explainMetric = (
    key: SemenMetricInfoKey,
    label: string,
    value: string | null | undefined,
    placeholder = notTestedLabel,
    icon?: LucideIcon,
  ) => buildSemenMetricExplanation(t, key, label, value, placeholder, icon);
  const explainOocyteMetric = (
    key: OocyteMetricInfoKey,
    label: string,
    value: string | null | undefined,
    placeholder = notTestedLabel,
    icon?: LucideIcon,
    sourceLinked = false,
  ) =>
    buildOocyteMetricExplanation(
      t,
      key,
      label,
      value,
      placeholder,
      icon,
      sourceLinked,
    );
  const sourceValue = specimen.source
    ? specimen.source === "self"
      ? t("cryo_vault.source_values.your_own")
      : specimen.source === "partner"
        ? t("cryo_vault.source_values.partner")
        : t("cryo_vault.source_values.donor")
    : null;
  const headerSourceValue =
    specimen.source && specimen.source !== "self" ? sourceValue : null;
  const pgtSummary = compactParts([
    specimen.pgtPerformed ? formatEnumValue(specimen.pgtPerformed) : null,
    pgt ? t(pgt.labelKey) : null,
  ]);
  const recordedThawCount =
    specimen.numberThawed ??
    (specimen.usageEvents?.filter((event) => event.usage === "thaw").length ||
      null);
  const recordedFertilizationCount =
    specimen.fertilizedCount ??
    (specimen.usageEvents?.filter((event) =>
      ["fertilize", "use"].includes(event.usage),
    ).length ||
      null);
  const oocyteUseSummary = compactParts([
    recordedThawCount != null && recordedThawCount > 0
      ? t("cryo_vault.modal.thawed_for_fertilization_value", {
          params: { count: String(recordedThawCount) },
        })
      : null,
    recordedFertilizationCount != null
      ? t("cryo_vault.modal.fertilized_count_value", {
          params: { count: String(recordedFertilizationCount) },
        })
      : null,
    embryosCreatedFromOocyte.length > 0
      ? t("cryo_vault.modal.embryos_created_count_value", {
          params: { count: String(embryosCreatedFromOocyte.length) },
        })
      : null,
  ]);
  const cohortSummaryItems = [
    {
      key: "total-retrieved",
      label:
        specimen.totalRetrieved != null
          ? t("cryo_vault.modal.total_retrieved_value", {
              params: { count: String(specimen.totalRetrieved) },
            })
          : null,
    },
    {
      key: "mature-mii",
      label:
        specimen.matureMII != null
          ? t("cryo_vault.modal.mature_mii_value", {
              params: { count: String(specimen.matureMII) },
            })
          : null,
    },
    {
      key: "immature-mi",
      label:
        specimen.immatureMI != null
          ? t("cryo_vault.modal.immature_mi_value", {
              params: { count: String(specimen.immatureMI) },
            })
          : null,
    },
    {
      key: "degenerated",
      label:
        specimen.degenerated != null
          ? t("cryo_vault.modal.degenerated_value", {
              params: { count: String(specimen.degenerated) },
            })
          : null,
    },
  ].filter((item): item is { key: string; label: string } =>
    Boolean(item.label),
  );
  const cohortSummary = compactParts(
    cohortSummaryItems.map((item) => item.label),
  );
  const remainingFrozenOocytes =
    specimen.type === "oocyte" && specimen.numberFrozen != null
      ? Math.max(0, specimen.numberFrozen - (specimen.numberThawed ?? 0))
      : null;
  const maturityRate =
    specimen.type === "oocyte" &&
    specimen.totalRetrieved != null &&
    specimen.totalRetrieved > 0 &&
    specimen.matureMII != null
      ? `${Math.round((specimen.matureMII / specimen.totalRetrieved) * 100)}%`
      : null;
  const predictiveReportSource = compactParts([
    specimen.predictiveReportName,
    specimen.predictiveReportProvider,
    predictiveReportDateFormatted,
  ]);
  const embryoRows: DetailItem[] =
    specimen.type === "embryo"
      ? [
          {
            key: "embryo-id",
            icon: Hash,
            label: t("cryo_vault.modal.embryo_id"),
            value: specimen.embryoId,
          },
          {
            key: "day",
            icon: Activity,
            label: t("cryo_vault.modal.day_of_development"),
            value: specimen.dayOfDevelopment
              ? t("cryo_vault.modal.day_value", {
                  params: { day: String(specimen.dayOfDevelopment) },
                })
              : null,
          },
          {
            key: "fertilized",
            icon: Calendar,
            label: t("cryo_vault.modal.fertilized"),
            value: fertilizationDateFormatted
              ? `${fertilizationDateFormatted}${specimen.fertilizationMethod ? ` · ${specimen.fertilizationMethod}` : ""}`
              : specimen.fertilizationMethod,
          },
          {
            key: "pgt",
            icon: Shield,
            label: t("cryo_vault.modal.pgt"),
            value: pgtSummary,
          },
          {
            key: "pgt-report",
            icon: FileText,
            label: t("cryo_vault.modal.pgt_report"),
            value: pgtReportDateFormatted
              ? `${pgtReportDateFormatted}${specimen.pgtReportSummary ? ` · ${specimen.pgtReportSummary}` : ""}`
              : specimen.pgtReportSummary,
          },
          {
            key: "sex",
            icon: UserRound,
            label: embryoSexLabel,
            value:
              embryoSex && sexDisclosure.hidden
                ? t("care.embryo_sex.show_embryo_sex")
                : embryoSex?.value,
            onPress:
              embryoSex && sexDisclosure.hidden
                ? sexDisclosure.reveal
                : undefined,
          },
        ]
      : [];
  const oocyteSpecimenRows: ClinicalMetricItem[] =
    specimen.type === "oocyte"
      ? [
          ...(!isOocyteCohort
            ? [
                {
                  key: "maturity",
                  icon: Beaker,
                  label: t("cryo_vault.modal.maturity"),
                  value: specimen.grade
                    ? formatEnumValue(specimen.grade)
                    : null,
                  placeholder: notTestedLabel,
                  explanation: explainOocyteMetric(
                    "oocyte_maturity",
                    t("cryo_vault.modal.maturity"),
                    specimen.grade ? formatEnumValue(specimen.grade) : null,
                    notTestedLabel,
                    Beaker,
                  ),
                },
              ]
            : []),
          {
            key: "freezing-method",
            icon: Snowflake,
            label: t("cryo_vault.modal.freezing_method"),
            value: formatEnumValue(specimen.freezingMethod),
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_freezing_method",
              t("cryo_vault.modal.freezing_method"),
              formatEnumValue(specimen.freezingMethod),
              notTestedLabel,
              Snowflake,
            ),
          },
          {
            key: "retrieval-date",
            icon: Calendar,
            label: t("cryo_vault.modal.retrieval_date"),
            value: retrievalDateFormatted ?? collectedDateFormatted,
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_retrieval_date",
              t("cryo_vault.modal.retrieval_date"),
              retrievalDateFormatted ?? collectedDateFormatted,
              notTestedLabel,
              Calendar,
            ),
          },
          {
            key: "frozen-date",
            icon: Snowflake,
            label: t("cryo_vault.modal.frozen"),
            value: frozenDateFormatted,
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_frozen_date",
              t("cryo_vault.modal.frozen"),
              frozenDateFormatted,
              notTestedLabel,
              Snowflake,
            ),
          },
        ]
      : [];
  const oocyteCohortRows: ClinicalMetricItem[] =
    specimen.type === "oocyte"
      ? [
          {
            key: "total-retrieved",
            icon: Hash,
            label: t("cryo_vault.modal.total_retrieved"),
            value: formatNumber(specimen.totalRetrieved),
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_total_retrieved",
              t("cryo_vault.modal.total_retrieved"),
              formatNumber(specimen.totalRetrieved),
              notTestedLabel,
              Hash,
            ),
          },
          {
            key: "mature-mii",
            icon: Beaker,
            label: t("cryo_vault.modal.mature_mii"),
            value: formatNumber(specimen.matureMII),
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_mature_mii",
              t("cryo_vault.modal.mature_mii"),
              formatNumber(specimen.matureMII),
              notTestedLabel,
              Beaker,
            ),
          },
          {
            key: "number-frozen",
            icon: Snowflake,
            label: t("cryo_vault.modal.number_frozen"),
            value: formatNumber(specimen.numberFrozen),
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_number_frozen",
              t("cryo_vault.modal.number_frozen"),
              formatNumber(specimen.numberFrozen),
              notTestedLabel,
              Snowflake,
            ),
          },
          {
            key: "number-thawed",
            icon: Activity,
            label: t("cryo_vault.modal.number_thawed"),
            value: formatNumber(specimen.numberThawed),
            placeholder: t("cryo_vault.modal.no_thaw_recorded"),
            explanation: explainOocyteMetric(
              "oocyte_number_thawed",
              t("cryo_vault.modal.number_thawed"),
              formatNumber(specimen.numberThawed),
              t("cryo_vault.modal.no_thaw_recorded"),
              Activity,
            ),
          },
          {
            key: "remaining-frozen",
            icon: Shield,
            label: t("cryo_vault.modal.remaining_frozen"),
            value: formatNumber(remainingFrozenOocytes),
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_remaining_frozen",
              t("cryo_vault.modal.remaining_frozen"),
              formatNumber(remainingFrozenOocytes),
              notTestedLabel,
              Shield,
            ),
          },
          {
            key: "maturity-rate",
            icon: Calculator,
            label: t("cryo_vault.modal.maturity_rate"),
            value: maturityRate,
            placeholder: notTestedLabel,
            explanation: explainOocyteMetric(
              "oocyte_maturity_rate",
              t("cryo_vault.modal.maturity_rate"),
              maturityRate,
              notTestedLabel,
              Calculator,
            ),
          },
          {
            key: "cycle",
            icon: Calendar,
            label: t("cryo_vault.modal.origin_cycle"),
            value:
              specimen.cycleLabel ??
              (specimen.cycleId
                ? t("cryo_vault.modal.cycle_linked_value")
                : null),
            onPress: originCycleId
              ? getCycleNavigation?.(originCycleId)
              : undefined,
          },
        ]
      : [];
  const oocyteOutlookRows: ClinicalMetricItem[] =
    specimen.type === "oocyte"
      ? [
          {
            key: "predicted-quality",
            icon: Gauge,
            label: t("cryo_vault.modal.predicted_egg_quality"),
            value: specimen.predictedEggQuality,
            explanation: explainOocyteMetric(
              "oocyte_predictive_quality",
              t("cryo_vault.modal.predicted_egg_quality"),
              specimen.predictedEggQuality,
              notTestedLabel,
              Gauge,
              Boolean(predictiveReportSource),
            ),
          },
          {
            key: "predicted-outlook",
            icon: Calculator,
            label: t("cryo_vault.modal.predicted_fertility_outlook"),
            value: specimen.predictedFertilityOutlook,
            explanation: explainOocyteMetric(
              "oocyte_predictive_outlook",
              t("cryo_vault.modal.predicted_fertility_outlook"),
              specimen.predictedFertilityOutlook,
              notTestedLabel,
              Calculator,
              Boolean(predictiveReportSource),
            ),
          },
        ]
      : [];
  const collectionMethodValue = formatEnumValue(specimen.collectionMethod);
  const processingMethodValue = formatEnumValue(specimen.processingMethod);
  const preparationMethodValue = formatEnumValue(specimen.preparationMethod);
  const abstinenceValue =
    specimen.abstinencePeriod != null
      ? t("cryo_vault.modal.abstinence_value", {
          params: { count: String(specimen.abstinencePeriod) },
        })
      : null;
  const spermRows: DetailItem[] =
    specimen.type === "sperm"
      ? [
          {
            key: "analysis-date",
            icon: Calendar,
            label: t("cryo_vault.modal.analysis_date"),
            value: analysisDateFormatted,
            explanation: explainMetric(
              "analysis_date",
              t("cryo_vault.modal.analysis_date"),
              analysisDateFormatted,
              notTestedLabel,
              Calendar,
            ),
          },
          {
            key: "collection-method",
            icon: TestTube,
            label: t("cryo_vault.modal.collection_method"),
            value: collectionMethodValue,
            explanation: explainMetric(
              "collection_method",
              t("cryo_vault.modal.collection_method"),
              collectionMethodValue,
              notTestedLabel,
              TestTube,
            ),
          },
          {
            key: "processing-method",
            icon: FlaskConical,
            label: t("cryo_vault.modal.processing_method"),
            value: processingMethodValue,
            explanation: explainMetric(
              "processing_method",
              t("cryo_vault.modal.processing_method"),
              processingMethodValue,
              notTestedLabel,
              FlaskConical,
            ),
          },
          {
            key: "preparation-method",
            icon: Beaker,
            label: t("cryo_vault.modal.preparation_method"),
            value: preparationMethodValue,
            explanation: explainMetric(
              "preparation_method",
              t("cryo_vault.modal.preparation_method"),
              preparationMethodValue,
              notTestedLabel,
              Beaker,
            ),
          },
          {
            key: "abstinence",
            icon: Clock,
            label: t("cryo_vault.modal.abstinence"),
            value: abstinenceValue,
            explanation: explainMetric(
              "abstinence",
              t("cryo_vault.modal.abstinence"),
              abstinenceValue,
              notTestedLabel,
              Clock,
            ),
          },
          {
            key: "other-sample-details",
            icon: FileText,
            label: t("cryo_vault.modal.other_sample_details"),
            value: specimen.otherSampleDetails,
          },
        ]
      : [];
  const semenAnalysisRows: ClinicalMetricItem[] =
    specimen.type === "sperm"
      ? [
          {
            key: "volume",
            icon: Droplets,
            label: t("cryo_vault.modal.volume"),
            value: specimen.volume,
            explanation: explainMetric(
              "volume",
              t("cryo_vault.modal.volume"),
              specimen.volume,
              notTestedLabel,
              Droplets,
            ),
          },
          {
            key: "concentration",
            icon: Gauge,
            label: t("cryo_vault.modal.concentration"),
            value: specimen.concentration,
            explanation: explainMetric(
              "concentration",
              t("cryo_vault.modal.concentration"),
              specimen.concentration,
              notTestedLabel,
              Gauge,
            ),
          },
          {
            key: "total-count",
            icon: Hash,
            label: t("cryo_vault.modal.total_count"),
            value: specimen.totalCount,
            explanation: explainMetric(
              "total_count",
              t("cryo_vault.modal.total_count"),
              specimen.totalCount,
              notTestedLabel,
              Hash,
            ),
          },
          {
            key: "tmsc",
            icon: Calculator,
            label: t("cryo_vault.modal.tmsc"),
            value: specimen.tmsc,
            explanation: explainMetric(
              "tmsc",
              t("cryo_vault.modal.tmsc"),
              specimen.tmsc,
              notTestedLabel,
              Calculator,
            ),
          },
          {
            key: "motility",
            icon: Activity,
            label: t("cryo_vault.modal.motility"),
            value: specimen.motility,
            explanation: explainMetric(
              "motility",
              t("cryo_vault.modal.motility"),
              specimen.motility,
              notTestedLabel,
              Activity,
            ),
          },
          {
            key: "progressive-motility",
            icon: ArrowRight,
            label: t("cryo_vault.modal.progressive_motility"),
            value: specimen.progressiveMotility,
            explanation: explainMetric(
              "progressive_motility",
              t("cryo_vault.modal.progressive_motility"),
              specimen.progressiveMotility,
              notTestedLabel,
              ArrowRight,
            ),
          },
          {
            key: "non-progressive-motility",
            icon: Activity,
            label: t("cryo_vault.modal.non_progressive_motility"),
            value: specimen.nonProgressiveMotility,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "non_progressive_motility",
              t("cryo_vault.modal.non_progressive_motility"),
              specimen.nonProgressiveMotility,
              notTestedLabel,
              Activity,
            ),
          },
          {
            key: "immotile",
            icon: Clock,
            label: t("cryo_vault.modal.immotile"),
            value: specimen.immotile,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "immotile",
              t("cryo_vault.modal.immotile"),
              specimen.immotile,
              notTestedLabel,
              Clock,
            ),
          },
          {
            key: "morphology",
            icon: Microscope,
            label: t("cryo_vault.modal.morphology"),
            value: specimen.morphology,
            explanation: explainMetric(
              "morphology",
              t("cryo_vault.modal.morphology"),
              specimen.morphology,
              notTestedLabel,
              Microscope,
            ),
          },
          {
            key: "agglutination-aggregation",
            icon: Hash,
            label: t("cryo_vault.modal.agglutination_aggregation"),
            value: specimen.agglutinationAggregation,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "agglutination_aggregation",
              t("cryo_vault.modal.agglutination_aggregation"),
              specimen.agglutinationAggregation,
              notTestedLabel,
              Hash,
            ),
          },
          {
            key: "ph",
            icon: FlaskConical,
            label: t("cryo_vault.modal.ph"),
            value: specimen.ph,
            explanation: explainMetric(
              "ph",
              t("cryo_vault.modal.ph"),
              specimen.ph,
              notTestedLabel,
              FlaskConical,
            ),
          },
          {
            key: "viscosity",
            icon: Droplets,
            label: t("cryo_vault.modal.viscosity"),
            value: specimen.viscosity,
            explanation: explainMetric(
              "viscosity",
              t("cryo_vault.modal.viscosity"),
              specimen.viscosity,
              notTestedLabel,
              Droplets,
            ),
          },
        ]
      : [];
  const advancedSemenRows: ClinicalMetricItem[] =
    specimen.type === "sperm"
      ? [
          {
            key: "vitality",
            icon: Shield,
            label: t("cryo_vault.modal.vitality"),
            value: specimen.vitality,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "vitality",
              t("cryo_vault.modal.vitality"),
              specimen.vitality,
              notTestedLabel,
              Shield,
            ),
          },
          {
            key: "leukocytes",
            icon: Microscope,
            label: t("cryo_vault.modal.leukocytes"),
            value: specimen.leukocytes,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "leukocytes",
              t("cryo_vault.modal.leukocytes"),
              specimen.leukocytes,
              notTestedLabel,
              Microscope,
            ),
          },
          {
            key: "dna-fragmentation-index",
            icon: Microscope,
            label: t("cryo_vault.modal.dna_fragmentation_index"),
            value: specimen.dnaFragmentationIndex,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "dna_fragmentation_index",
              t("cryo_vault.modal.dna_fragmentation_index"),
              specimen.dnaFragmentationIndex,
              notTestedLabel,
              Microscope,
            ),
          },
          {
            key: "antisperm-antibody",
            icon: Shield,
            label: t("cryo_vault.modal.antisperm_antibody"),
            value: specimen.antispermAntibody,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "antisperm_antibody",
              t("cryo_vault.modal.antisperm_antibody"),
              specimen.antispermAntibody,
              notTestedLabel,
              Shield,
            ),
          },
          {
            key: "sperm-epigenetics",
            icon: FileText,
            label: t("cryo_vault.modal.sperm_epigenetics"),
            value: specimen.spermEpigenetics,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "sperm_epigenetics",
              t("cryo_vault.modal.sperm_epigenetics"),
              specimen.spermEpigenetics,
              notTestedLabel,
              FileText,
            ),
          },
          {
            key: "oxidative-stress",
            icon: FlaskConical,
            label: t("cryo_vault.modal.oxidative_stress"),
            value: specimen.oxidativeStress,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "oxidative_stress",
              t("cryo_vault.modal.oxidative_stress"),
              specimen.oxidativeStress,
              notTestedLabel,
              FlaskConical,
            ),
          },
          {
            key: "sperm-function",
            icon: ArrowRight,
            label: t("cryo_vault.modal.sperm_function"),
            value: specimen.spermFunction,
            placeholder: notTestedLabel,
            explanation: explainMetric(
              "sperm_function",
              t("cryo_vault.modal.sperm_function"),
              specimen.spermFunction,
              notTestedLabel,
              ArrowRight,
            ),
          },
        ]
      : [];
  const storageRows: DetailItem[] = [
    {
      key: "facility",
      icon: MapPin,
      label: t("cryo_vault.modal.facility"),
      value: editableValue(specimen.facility, "facility"),
      onPress: canEditField("facility")
        ? () => setEditingField("facility")
        : undefined,
    },
    {
      key: "storage-locator",
      icon: Snowflake,
      label: t("cryo_vault.modal.storage_locator"),
      value: editableValue(storageLocator, "storage_locator"),
      onPress: canEditField("storage_locator")
        ? () => setEditingField("storage_locator")
        : undefined,
    },
    {
      key: "storage-context",
      icon: MapPin,
      label: t("cryo_vault.modal.storage_context"),
      value: storageContext,
    },
    {
      key: "sample-id",
      icon: Hash,
      label: t("cryo_vault.modal.sample_id"),
      value:
        specimen.type === "sperm"
          ? editableValue(specimen.sampleId, "sample_id")
          : null,
      onPress:
        specimen.type === "sperm" && canEditField("sample_id")
          ? () => setEditingField("sample_id")
          : undefined,
    },
  ];
  const tabOptions: {
    value: SpecimenDetailTab;
    label: string;
    shortLabel?: string;
    icon: LucideIcon;
  }[] = [
    {
      value: "summary",
      label: t("cryo_vault.modal.tab_summary"),
      icon: Info,
    },
    ...(specimen.type === "sperm" || specimen.type === "oocyte"
      ? []
      : [
          {
            value: "clinical" as const,
            label: t("cryo_vault.modal.tab_reports"),
            shortLabel: t("cryo_vault.modal.tab_reports_short"),
            icon: Microscope,
          },
        ]),
    {
      value: "storage",
      label: t("cryo_vault.modal.tab_storage"),
      shortLabel: t("cryo_vault.modal.tab_storage_short"),
      icon: Snowflake,
    },
    {
      value: "usage",
      label: t("cryo_vault.modal.tab_usage"),
      icon: Beaker,
    },
    {
      value: "documents",
      label: t("cryo_vault.modal.tab_documents"),
      icon: FileText,
    },
  ];
  const selectedTab =
    (specimen.type === "sperm" || specimen.type === "oocyte") &&
    activeTab === "clinical"
      ? "summary"
      : activeTab;
  const cycleHeaderTag = specimen.cycleId
    ? getCycleHeaderTag(t, specimen.cycleLabel)
    : null;
  const recordKindTag = isOocyteCohort
    ? t("cryo_vault.modal.egg_cohort_tag")
    : null;
  const headerTags = [
    recordKindTag,
    statusLabel,
    headerSourceValue,
    cycleHeaderTag,
  ].filter((tag): tag is string => Boolean(tag));
  const cycleLabelParts = getCycleMetadataTags(specimen.cycleLabel);
  const hasUsageContent =
    Boolean(specimen.cycleId || specimen.cycleLabel) ||
    Boolean(specimen.useCycleId || specimen.useCycleLabel) ||
    Boolean(specimen.transferCycleId || specimen.transferCycleLabel) ||
    Boolean(
      specimen.usageEvents?.some((event) =>
        ["thaw", "fertilize", "use", "transfer"].includes(event.usage),
      ),
    ) ||
    embryosCreatedFromOocyte.length > 0 ||
    (specimen.type === "embryo" &&
      Boolean(specimen.sourceEggId || specimen.sourceSpermId));
  const handlePlanTreatmentPress = () => {
    onPlanTreatment?.(specimen);
  };
  const handleDocumentActionPress = (item: {
    key: string;
    title: string;
    body: string;
  }) => {
    onClose();
    openDocumentUpload({
      slot: {
        id: `cryo-specimen-${specimen.id}-${item.key}`,
        name: item.title,
        why: item.body,
      },
      defaultCategory: "medical",
      defaultTags: [
        "cryo-vault",
        "specimen",
        `specimen:${specimen.id}`,
        `specimen-type:${specimen.type}`,
        `specimen-document:${item.key}`,
      ],
    });
  };

  const handleDeleteConfirmed = async () => {
    if (!onDelete) return;
    setDeleteError(null);
    setDeletePending(true);
    try {
      await onDelete(specimen);
      setDeleteConfirmationVisible(false);
    } catch {
      setDeleteError(t("cryo_vault.modal.delete_specimen_record_failed"));
    } finally {
      setDeletePending(false);
    }
  };
  const handleRecordDispositionInstead = (
    disposition: "use" | "discard" | "donate",
  ) => {
    setDeleteError(null);
    setDeleteConfirmationVisible(false);
    if (disposition === "use") {
      // How it was used decides what is recorded, so she picks it from the
      // same question the event picker asks.
      setLifecycleActionPickerVisible(true);
      return;
    }
    setLifecycleChoice(
      getLifecycleChoicesForSpecimen(specimen).outcomes.find(
        (choice) => choice.action === disposition,
      ) ?? null,
    );
  };
  const primaryActionLabel =
    specimen.type === "embryo"
      ? t("cryo_vault.modal.plan_transfer_button")
      : specimen.type === "oocyte"
        ? isOocyteCohort
          ? t("cryo_vault.modal.use_remaining_eggs_button")
          : t("cryo_vault.modal.fertilize_button")
        : t("cryo_vault.modal.use_semen_button");
  const originCycleAction = originCycleId
    ? getCycleNavigation?.(originCycleId)
    : undefined;
  const transferCycleAction = transferCycleId
    ? getCycleNavigation?.(transferCycleId)
    : undefined;
  const originCycleCompactLabel = getCompactCycleLabel(t, specimen.cycleLabel);
  const transferCycleCompactLabel = getCompactCycleLabel(
    t,
    specimen.transferCycleLabel,
  );
  const originCycleNumber = originCycleCompactLabel?.match(/#\s*(\d+)/)?.[1];
  const transferCycleNumber =
    transferCycleCompactLabel?.match(/#\s*(\d+)/)?.[1];
  const originCycleActionLabel = originCycleNumber
    ? t("cryo_vault.modal.origin_cycle_action", {
        params: { number: originCycleNumber },
      })
    : t("cryo_vault.modal.origin_cycle");
  const transferCycleActionLabel = transferCycleNumber
    ? t("cryo_vault.modal.transfer_cycle_action", {
        params: { number: transferCycleNumber },
      })
    : t("cryo_vault.modal.transfer_cycle");
  const hasTransferCycle = Boolean(
    transferCycleId || specimen.transferCycleLabel,
  );
  const hasPhysicallyConsumedEvent = (specimen.specimenUsages ?? []).some(
    (usage) => PHYSICALLY_CONSUMED_SPECIMEN_USAGES.has(usage.usage),
  );
  const hasSelectableCohortMembers =
    !isOocyteCohort ||
    ((specimen.cohortMemberSpecimenIds?.length ?? 0) > 0 &&
      remainingFrozenOocytes !== 0);
  const hasMaterialAvailableForTreatment = isOocyteCohort
    ? (specimen.status === "stored" || specimen.status === "partially_used") &&
      hasSelectableCohortMembers
    : specimen.status === "stored" && !hasPhysicallyConsumedEvent;
  const canPlanTreatment =
    Boolean(onPlanTreatment) &&
    hasMaterialAvailableForTreatment &&
    !(specimen.type === "embryo" && hasTransferCycle);
  const primaryFooterAction = hasTransferCycle
    ? transferCycleAction
    : canPlanTreatment
      ? handlePlanTreatmentPress
      : undefined;
  const primaryFooterActionLabel = hasTransferCycle
    ? transferCycleActionLabel
    : canPlanTreatment
      ? primaryActionLabel
      : undefined;
  const explicitCycleRelationships = Array.from(
    new Map(
      (specimen.cycleRelationships ?? [])
        .filter((relationship) => relationship.relation !== "origin")
        .map((relationship) => [
          `${relationship.relation}:${relationship.cycleId}`,
          relationship,
        ]),
    ).values(),
  );
  const explicitCycleRelationshipKeys = new Set(
    explicitCycleRelationships.map(
      (relationship) => `${relationship.relation}:${relationship.cycleId}`,
    ),
  );
  const hasExplicitUseCycle = explicitCycleRelationships.some(
    (relationship) => relationship.relation === "used_in",
  );
  const hasExplicitTransferCycle = explicitCycleRelationships.some(
    (relationship) => relationship.relation === "transfer",
  );
  const explicitCycleRelationshipRows: DetailItem[] =
    explicitCycleRelationships.map((relationship) => {
      const isUsedInCycle = relationship.relation === "used_in";
      const isTransferCycle = relationship.relation === "transfer";
      const knownLabel = isUsedInCycle
        ? relationship.cycleId === usedInCycleId
          ? specimen.useCycleLabel
          : null
        : isTransferCycle && relationship.cycleId === transferCycleId
          ? specimen.transferCycleLabel
          : null;

      return {
        key: `cycle-relationship-${relationship.relation}-${relationship.cycleId}`,
        icon: isTransferCycle ? ArrowRight : Beaker,
        label: isUsedInCycle
          ? t("cryo_vault.modal.used_in_cycle")
          : t("cryo_vault.modal.transfer_cycle"),
        value: knownLabel ?? t("cryo_vault.modal.cycle_linked_value"),
        onPress: getCycleNavigation?.(relationship.cycleId),
      };
    });
  const cycleRelationshipRows: DetailItem[] = [
    ...explicitCycleRelationshipRows,
    ...(usedInCycleId &&
    !explicitCycleRelationshipKeys.has(`used_in:${usedInCycleId}`)
      ? [
          {
            key: `cycle-relationship-used_in-${usedInCycleId}`,
            icon: Beaker,
            label: t("cryo_vault.modal.used_in_cycle"),
            value:
              specimen.useCycleLabel ??
              t("cryo_vault.modal.cycle_linked_value"),
            onPress: getCycleNavigation?.(usedInCycleId),
          },
        ]
      : []),
    ...(!usedInCycleId && !hasExplicitUseCycle && specimen.useCycleLabel
      ? [
          {
            key: "use-cycle-unresolved",
            icon: Beaker,
            label: t("cryo_vault.modal.used_in_cycle"),
            value: specimen.useCycleLabel,
          },
        ]
      : []),
    ...(transferCycleId &&
    !explicitCycleRelationshipKeys.has(`transfer:${transferCycleId}`)
      ? [
          {
            key: `cycle-relationship-transfer-${transferCycleId}`,
            icon: ArrowRight,
            label: t("cryo_vault.modal.transfer_cycle"),
            value:
              specimen.transferCycleLabel ??
              t("cryo_vault.modal.transfer_cycle_linked_value"),
            onPress: getCycleNavigation?.(transferCycleId),
          },
        ]
      : []),
    ...(!transferCycleId &&
    !hasExplicitTransferCycle &&
    specimen.transferCycleLabel
      ? [
          {
            key: "transfer-cycle-unresolved",
            icon: ArrowRight,
            label: t("cryo_vault.modal.transfer_cycle"),
            value: specimen.transferCycleLabel,
          },
        ]
      : []),
  ];
  // Derive the treatment-use summary for semen from the usage-event log, the
  // same log the vault reads to move a used sample to Vault history.
  const spermUseEvent = (specimen.usageEvents ?? []).find(
    (event) => event.usage === "use" || event.usage === "fertilize",
  );
  const spermUseCycleLabel = spermUseEvent?.cycleId
    ? (specimen.useCycleLabel ?? t("cryo_vault.modal.cycle_linked_value"))
    : null;
  const spermUseSummary = spermUseEvent
    ? spermUseCycleLabel
      ? t("cryo_vault.modal.semen_used_in_cycle", {
          params: { cycle: spermUseCycleLabel },
        })
      : t("cryo_vault.modal.semen_used_in_treatment")
    : null;
  const treatmentUseState =
    specimen.type === "embryo"
      ? t("cryo_vault.modal.embryo_use_state")
      : specimen.type === "oocyte"
        ? (oocyteUseSummary ?? t("cryo_vault.modal.oocyte_use_state"))
        : (spermUseSummary ?? t("cryo_vault.modal.semen_use_state"));
  const usageRows: DetailItem[] = [
    {
      key: "current-state",
      icon: Shield,
      label: isOocyteCohort
        ? t("cryo_vault.modal.cohort_state")
        : t("cryo_vault.modal.current_state"),
      value: statusLabel,
    },
    ...(specimen.type === "embryo" && hasTransferCycle
      ? []
      : [
          {
            key: "treatment-use",
            icon: Beaker,
            label: isOocyteCohort
              ? t("cryo_vault.modal.cohort_use")
              : t("cryo_vault.modal.treatment_use"),
            value: hasUsageContent ? treatmentUseState : null,
          },
        ]),
    {
      key: "origin-cycle",
      icon: Calendar,
      label: t("cryo_vault.modal.origin_cycle"),
      value:
        specimen.cycleLabel ??
        (specimen.cycleId ? t("cryo_vault.modal.cycle_linked_value") : null),
      onPress: originCycleId ? getCycleNavigation?.(originCycleId) : undefined,
    },
    ...(specimen.type === "embryo" &&
    originCycleId &&
    (specimen.pgtReportDate || specimen.pgtReportSummary)
      ? [
          {
            key: "embryo-report",
            icon: Dna,
            label: t("cryo_vault.modal.embryo_report"),
            value: t("cryo_vault.modal.view_embryo_report"),
            onPress: getEmbryoReportNavigation?.(originCycleId),
          },
        ]
      : []),
    ...cycleRelationshipRows,
  ];
  const oocyteUseCohortRows: ClinicalMetricItem[] =
    specimen.type === "oocyte"
      ? [
          {
            key: "retrieval-cohort",
            icon: Hash,
            label: t("cryo_vault.modal.retrieval_cohort"),
            value: cohortSummary,
            placeholder: t("cryo_vault.modal.no_cohort_counts"),
            children:
              cohortSummaryItems.length > 0 ? (
                <ValueGridTagGroup
                  labels={cohortSummaryItems.map((item) => item.label)}
                />
              ) : undefined,
          },
          ...(isOocyteCohort
            ? [
                {
                  key: "number-thawed",
                  icon: Activity,
                  label: t("cryo_vault.modal.number_thawed"),
                  value: formatNumber(specimen.numberThawed),
                  placeholder: t("cryo_vault.modal.no_thaw_recorded"),
                },
              ]
            : []),
          {
            key: "fertilized-count",
            icon: Beaker,
            label: t("cryo_vault.modal.fertilized_count"),
            value: formatNumber(specimen.fertilizedCount),
          },
          {
            key: "embryos-created",
            icon: FlaskConical,
            label: t("cryo_vault.modal.embryos_created"),
            value:
              embryosCreatedFromOocyte.length > 0
                ? String(embryosCreatedFromOocyte.length)
                : null,
          },
          {
            key: "remaining",
            icon: Snowflake,
            label: t("cryo_vault.modal.remaining_frozen"),
            value: formatNumber(remainingFrozenOocytes),
          },
        ]
      : [];
  const semenDocumentRequirements =
    specimen.type === "sperm"
      ? [
          {
            key: "semen-analysis-report",
            title: sourceDocumentName,
            body: t("cryo_vault.modal.semen_analysis_report_body"),
          },
          {
            key: "consent-to-use",
            title: t("cryo_vault.modal.consent_to_use_document"),
            body: t("cryo_vault.modal.consent_to_use_document_body"),
          },
          {
            key: "storage-agreement",
            title: t("cryo_vault.modal.cryostorage_agreement_document"),
            body: t("cryo_vault.modal.cryostorage_agreement_document_body"),
          },
          {
            key: "chain-of-custody",
            title: t("cryo_vault.modal.chain_of_custody_document"),
            body: t("cryo_vault.modal.chain_of_custody_document_body"),
          },
        ]
      : [];
  const oocyteDocumentRequirements =
    specimen.type === "oocyte"
      ? [
          {
            key: "retrieval-report",
            title: t("cryo_vault.modal.retrieval_report"),
            body: t("cryo_vault.modal.retrieval_report_body"),
          },
          {
            key: "embryology-lab-report",
            title: t("cryo_vault.modal.embryology_lab_report"),
            body: t("cryo_vault.modal.embryology_lab_report_body"),
          },
          {
            key: "predictive-report",
            title: t("cryo_vault.modal.predictive_fertility_report"),
            body: t("cryo_vault.modal.predictive_fertility_report_body"),
          },
          {
            key: "storage-agreement",
            title: t("cryo_vault.modal.cryostorage_agreement_document"),
            body: t("cryo_vault.modal.cryostorage_agreement_document_body"),
          },
          {
            key: "consent-to-use",
            title: t("cryo_vault.modal.consent_to_use_document"),
            body: t("cryo_vault.modal.consent_to_use_document_body"),
          },
          {
            key: "chain-of-custody",
            title: t("cryo_vault.modal.chain_of_custody_document"),
            body: t("cryo_vault.modal.chain_of_custody_document_body"),
          },
        ]
      : [];
  const embryoDocumentRequirements =
    specimen.type === "embryo"
      ? [
          {
            key: "embryology-report",
            title: sourceDocumentName,
            body: t("cryo_vault.modal.embryology_lab_report_body"),
          },
          {
            key: "consent-to-use",
            title: t("cryo_vault.modal.consent_to_use_document"),
            body: t("cryo_vault.modal.consent_to_use_document_body"),
          },
          {
            key: "storage-agreement",
            title: t("cryo_vault.modal.cryostorage_agreement_document"),
            body: t("cryo_vault.modal.cryostorage_agreement_document_body"),
          },
          {
            key: "chain-of-custody",
            title: t("cryo_vault.modal.chain_of_custody_document"),
            body: t("cryo_vault.modal.chain_of_custody_document_body"),
          },
        ]
      : [];

  const renderUsageContent = () => {
    // Holds placed by older cycles are not something she recorded.
    const usageHistory = (specimen.usageEvents ?? [])
      .filter((event) => event.usage !== "reserve" && event.usage !== "release")
      .sort((a, b) => {
        const aTimestamp = `${a.date ?? ""}T${a.time ?? ""}`;
        const bTimestamp = `${b.date ?? ""}T${b.time ?? ""}`;
        return bTimestamp.localeCompare(aTimestamp);
      });

    return (
      <>
        <DetailSection rows={usageRows} />
        {isOocyteCohort && !hasSelectableCohortMembers && (
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 16,
              color: EMBER.colors.textLight,
              fontSize: 12,
              lineHeight: 17,
            }}
          >
            {t("cryo_vault.modal.cohort_treatment_unavailable")}
          </Text>
        )}
        {!isOocyteCohort && hasUsageContent && (
          <View style={{ marginTop: 16 }}>
            <SecondaryButton
              label={t("cryo_vault.modal.record_lifecycle_event")}
              accessibilityLabel={t("cryo_vault.modal.record_lifecycle_event")}
              action="cryo_specimen_record_lifecycle_event"
              testID="record-cryo-specimen-lifecycle-event-button"
              leading={
                <Activity
                  size={16}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.8}
                />
              }
              onPress={() => setLifecycleActionPickerVisible(true)}
            />
          </View>
        )}

        {usageHistory.length > 0 && (
          <View style={{ marginTop: 16, gap: 10 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "700",
                color: EMBER.colors.textFaint,
                letterSpacing: 0.3,
                textTransform: "uppercase",
              }}
            >
              {t("cryo_vault.modal.lifecycle_history")}
            </Text>
            {usageHistory.map((event, index) => {
              const eventDate = formatSpecimenDate(event.date, locale);
              const eventLabel = formatEnumValue(event.usage) ?? event.usage;
              const eventWhen = [eventDate, event.time]
                .filter(Boolean)
                .join(" · ");
              return (
                <View
                  key={`${event.id ?? event.usage}-${event.date ?? "unknown"}-${index}`}
                  style={{ gap: 5 }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <Tag label={eventLabel} shape="pill" />
                    {eventWhen && (
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.textFaint,
                          fontSize: 11,
                          lineHeight: 15,
                        }}
                      >
                        {eventWhen}
                      </Text>
                    )}
                  </View>
                  {event.notes && (
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textLight,
                        fontSize: 12,
                        lineHeight: 17,
                      }}
                    >
                      {event.notes}
                    </Text>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {isOocyteCohort && (
          <ValueGridCard
            title={t("cryo_vault.modal.cohort_availability_section")}
            rows={oocyteUseCohortRows}
          />
        )}

        {specimen.type === "oocyte" && embryosCreatedFromOocyte.length > 0 && (
          <View style={{ marginTop: 16, gap: 8 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "700",
                color: EMBER.colors.textFaint,
                letterSpacing: 0.3,
                textTransform: "uppercase",
              }}
            >
              {isOocyteCohort
                ? t("cryo_vault.modal.embryos_created_from_cohort")
                : t("cryo_vault.modal.embryos_created")}
            </Text>
            {embryosCreatedFromOocyte.map((embryo) => (
              <SpecimenLinkRow
                key={embryo.id}
                specimen={embryo}
                subtitle={compactParts([
                  embryo.grade
                    ? t("cryo_vault.grade_prefix", {
                        params: { grade: embryo.grade },
                      })
                    : null,
                  embryo.cycleLabel,
                ])}
                tags={[
                  (embryo.embryoStatus ?? embryo.status)
                    ? formatEnumValue(embryo.embryoStatus ?? embryo.status)
                    : null,
                  embryo.transferCycleLabel,
                ].filter((tag): tag is string => Boolean(tag))}
                onPress={
                  onSelectSpecimen
                    ? () => onSelectSpecimen(embryo.id)
                    : undefined
                }
              />
            ))}
          </View>
        )}

        {specimen.type === "embryo" &&
          (specimen.sourceEggId || specimen.sourceSpermId) && (
            <View style={{ marginTop: 16, gap: 8 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: EMBER.colors.textFaint,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {t("cryo_vault.modal.created_from")}
              </Text>
              {specimen.sourceEggId && (
                <ProvenanceRow
                  specimenType="oocyte"
                  resolved={sourceEgg ?? null}
                  fallbackSource={specimen.sourceEggSource}
                  canNavigate={!!sourceEgg && !!onSelectSpecimen}
                  onPress={
                    sourceEgg && onSelectSpecimen
                      ? () => onSelectSpecimen?.(sourceEgg.id)
                      : undefined
                  }
                />
              )}
              {specimen.sourceSpermId && (
                <ProvenanceRow
                  specimenType="sperm"
                  resolved={sourceSperm ?? null}
                  fallbackSource={specimen.sourceSpermSource}
                  canNavigate={!!sourceSperm && !!onSelectSpecimen}
                  onPress={
                    sourceSperm && onSelectSpecimen
                      ? () => onSelectSpecimen?.(sourceSperm.id)
                      : undefined
                  }
                />
              )}
            </View>
          )}

        {!hasUsageContent && (
          <EmptyDetailCard
            title={t("cryo_vault.modal.usage_empty_title")}
            body={t("cryo_vault.modal.usage_empty_body")}
            action={
              !isOocyteCohort ? (
                <CompactActionButton
                  label={t("cryo_vault.modal.record_lifecycle_event")}
                  action="cryo_specimen_record_lifecycle_event"
                  variant="primary"
                  leadingIcon={Plus}
                  onPress={() => setLifecycleActionPickerVisible(true)}
                />
              ) : undefined
            }
          />
        )}
      </>
    );
  };

  const renderSemenSummaryContent = () => {
    const collectionDateLabel =
      specimen.type === "oocyte"
        ? t("cryo_vault.modal.retrieval_date")
        : t("cryo_vault.modal.collection_date");

    return (
      <>
        <DetailCard>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            <ValueGridTile
              label={t("cryo_vault.modal.your_notes")}
              value={editableValue(specimen.notes, "notes")}
              icon={FileText}
              fullWidth
              muted={!specimen.notes}
              onPress={
                canEditField("notes")
                  ? () => setEditingField("notes")
                  : undefined
              }
              pressIndicator="chevron"
            />
            <ValueGridTile
              label={t("cryo_vault.modal.status")}
              value={editableValue(statusLabel, "status")}
              icon={Shield}
              muted={!statusLabel}
              onPress={
                canEditField("status")
                  ? () => setEditingField("status")
                  : undefined
              }
              pressIndicator="chevron"
            />
            <ValueGridTile
              label={t("cryo_vault.modal.facility")}
              value={editableValue(specimen.facility, "facility")}
              icon={MapPin}
              muted={!specimen.facility}
              onPress={
                canEditField("facility")
                  ? () => setEditingField("facility")
                  : undefined
              }
              pressIndicator="chevron"
            />
            <ValueGridTile
              label={t("cryo_vault.modal.source")}
              value={sourceValue}
              icon={UserRound}
            />
            <ValueGridTile
              label={t("cryo_vault.modal.sample_id")}
              value={editableValue(specimen.sampleId, "sample_id")}
              icon={Hash}
              muted={!specimen.sampleId}
              onPress={
                canEditField("sample_id")
                  ? () => setEditingField("sample_id")
                  : undefined
              }
              pressIndicator="chevron"
            />
            <ValueGridTile
              label={t("cryo_vault.modal.frozen")}
              value={editableValue(frozenDateFormatted, "freeze_date")}
              icon={Snowflake}
              muted={!frozenDateFormatted}
              onPress={
                canEditField("freeze_date")
                  ? () => setEditingField("freeze_date")
                  : undefined
              }
              pressIndicator="chevron"
            />
            <ValueGridTile
              label={collectionDateLabel}
              value={editableValue(collectedDateFormatted, "collection_date")}
              icon={Calendar}
              muted={!collectedDateFormatted}
              onPress={
                canEditField("collection_date")
                  ? () => setEditingField("collection_date")
                  : undefined
              }
              pressIndicator="chevron"
            />
          </View>
        </DetailCard>
        <ClinicalMetricCard
          title={t("cryo_vault.modal.semen_analysis_section")}
          rows={semenAnalysisRows}
          onMetricPress={(row) =>
            setSelectedMetricExplanation(row.explanation ?? null)
          }
        />
        {semenAnalysisRows.every((row) => !row.value) && (
          <EmptyDetailCard
            title={t("cryo_vault.modal.semen_analysis_empty_title")}
            body={t("cryo_vault.modal.semen_analysis_empty_body")}
            action={
              <CompactActionButton
                label={t("cryo_vault.modal.upload_document")}
                action="cryo_specimen_upload_semen_analysis"
                variant="primary"
                leadingIcon={Upload}
                onPress={() =>
                  handleDocumentActionPress({
                    key: "semen-analysis-report",
                    title: t("cryo_vault.modal.semen_analysis_report"),
                    body: t("cryo_vault.modal.semen_analysis_report_body"),
                  })
                }
              />
            }
          />
        )}
        <ValueGridCard
          title={t("cryo_vault.modal.extended_semen_panel_section")}
          rows={advancedSemenRows}
          onMetricPress={(row) =>
            setSelectedMetricExplanation(row.explanation ?? null)
          }
        />
        <ValueGridCard
          title={t("cryo_vault.modal.sperm_section")}
          rows={spermRows}
          onMetricPress={(row) =>
            setSelectedMetricExplanation(row.explanation ?? null)
          }
        />
      </>
    );
  };

  const renderOocyteSummaryContent = () => (
    <>
      <DetailCard>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <ValueGridTile
            label={t("cryo_vault.modal.your_notes")}
            value={editableValue(specimen.notes, "notes")}
            icon={FileText}
            fullWidth
            muted={!specimen.notes}
            onPress={
              canEditField("notes") ? () => setEditingField("notes") : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.status")}
            value={editableValue(statusLabel, "status")}
            icon={Shield}
            muted={!statusLabel}
            onPress={
              canEditField("status")
                ? () => setEditingField("status")
                : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.facility")}
            value={editableValue(specimen.facility, "facility")}
            icon={MapPin}
            muted={!specimen.facility}
            onPress={
              canEditField("facility")
                ? () => setEditingField("facility")
                : undefined
            }
            pressIndicator="chevron"
          />
          {!isOocyteCohort && (
            <ValueGridTile
              label={t("cryo_vault.modal.identification_number")}
              value={editableValue(
                specimen.identificationNumber,
                "identification_number",
              )}
              icon={Hash}
              muted={!specimen.identificationNumber}
              onPress={
                canEditField("identification_number")
                  ? () => setEditingField("identification_number")
                  : undefined
              }
              pressIndicator="chevron"
            />
          )}
          <ValueGridTile
            label={t("cryo_vault.modal.source")}
            value={sourceValue}
            icon={UserRound}
          />
          <ValueGridTile
            label={t("cryo_vault.modal.retrieval_date")}
            value={
              retrievalDateFormatted ||
              collectedDateFormatted ||
              editableValue(
                retrievalDateFormatted || collectedDateFormatted,
                "collection_date",
              )
            }
            icon={Calendar}
            muted={!retrievalDateFormatted && !collectedDateFormatted}
            onPress={
              canEditField("collection_date")
                ? () => setEditingField("collection_date")
                : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.frozen")}
            value={editableValue(frozenDateFormatted, "freeze_date")}
            icon={Snowflake}
            muted={!frozenDateFormatted}
            onPress={
              canEditField("freeze_date")
                ? () => setEditingField("freeze_date")
                : undefined
            }
            pressIndicator="chevron"
          />
        </View>
      </DetailCard>

      <ClinicalMetricCard
        title={
          isOocyteCohort
            ? t("cryo_vault.modal.oocyte_cohort_section")
            : t("cryo_vault.modal.oocyte_section")
        }
        rows={oocyteSpecimenRows}
        onMetricPress={(row) =>
          setSelectedMetricExplanation(row.explanation ?? null)
        }
      />

      {isOocyteCohort && (
        <>
          <ValueGridCard
            title={t("cryo_vault.modal.retrieval_cohort_section")}
            rows={oocyteCohortRows}
            onMetricPress={(row) =>
              setSelectedMetricExplanation(row.explanation ?? null)
            }
          />

          <ValueGridCard
            title={t("cryo_vault.modal.report_backed_outlook_section")}
            rows={oocyteOutlookRows}
            onMetricPress={(row) =>
              setSelectedMetricExplanation(row.explanation ?? null)
            }
          >
            {specimen.predictiveReportAssumptions && (
              <View
                style={{
                  borderTopWidth: 1,
                  borderTopColor: semanticColors.border.subtle,
                  paddingTop: 10,
                  gap: 10,
                }}
              >
                <MoonDetailRow
                  icon={ListChecks}
                  label={t("cryo_vault.modal.report_assumptions")}
                  value={
                    <CardSubtitleText style={{ marginTop: 0 }}>
                      {specimen.predictiveReportAssumptions}
                    </CardSubtitleText>
                  }
                />
              </View>
            )}
          </ValueGridCard>
        </>
      )}
    </>
  );

  const renderEmbryoSummaryContent = () => (
    <>
      <DetailCard>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <ValueGridTile
            label={t("cryo_vault.modal.your_notes")}
            value={editableValue(specimen.notes, "notes")}
            icon={FileText}
            fullWidth
            muted={!specimen.notes}
            onPress={
              canEditField("notes") ? () => setEditingField("notes") : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.status")}
            value={editableValue(statusLabel, "status")}
            icon={Shield}
            muted={!statusLabel}
            onPress={
              canEditField("status")
                ? () => setEditingField("status")
                : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.facility")}
            value={editableValue(specimen.facility, "facility")}
            icon={MapPin}
            muted={!specimen.facility}
            onPress={
              canEditField("facility")
                ? () => setEditingField("facility")
                : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.frozen")}
            value={editableValue(frozenDateFormatted, "freeze_date")}
            icon={Snowflake}
            muted={!frozenDateFormatted}
            onPress={
              canEditField("freeze_date")
                ? () => setEditingField("freeze_date")
                : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.fertilized")}
            value={
              fertilizationDateFormatted
                ? `${fertilizationDateFormatted}${specimen.fertilizationMethod ? ` · ${specimen.fertilizationMethod}` : ""}`
                : editableValue(specimen.fertilizationMethod, "collection_date")
            }
            icon={Calendar}
            onPress={
              canEditField("collection_date")
                ? () => setEditingField("collection_date")
                : undefined
            }
            pressIndicator="chevron"
          />
          <ValueGridTile
            label={t("cryo_vault.modal.origin_cycle")}
            value={
              specimen.cycleLabel ??
              (specimen.cycleId
                ? t("cryo_vault.modal.cycle_linked_value")
                : null)
            }
            icon={Calendar}
            onPress={
              originCycleId ? getCycleNavigation?.(originCycleId) : undefined
            }
          />
        </View>
      </DetailCard>

      <DetailCard title={t("cryo_vault.modal.embryo_quality_section")}>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <ValueGridTile
            label={t("cryo_vault.modal.embryo_grade")}
            value={specimen.grade ?? notTestedLabel}
            icon={Microscope}
            muted={!specimen.grade}
          />
          <ValueGridTile
            label={t("cryo_vault.modal.day_of_development")}
            value={
              specimen.dayOfDevelopment
                ? t("cryo_vault.modal.day_value", {
                    params: { day: String(specimen.dayOfDevelopment) },
                  })
                : notTestedLabel
            }
            icon={Activity}
            muted={!specimen.dayOfDevelopment}
          />
          <ValueGridTile
            label={t("cryo_vault.modal.pgt")}
            value={pgtSummary ?? notTestedLabel}
            icon={Shield}
            muted={!pgtSummary}
            trailingIcon={
              pgtReportDateFormatted || specimen.pgtReportSummary
                ? FileText
                : undefined
            }
            onTrailingPress={
              pgtReportDateFormatted || specimen.pgtReportSummary
                ? () => setActiveTab("clinical")
                : undefined
            }
            trailingAccessibilityLabel={t("cryo_vault.modal.view_pgt_report")}
          />
          <ValueGridTile
            label={embryoSexLabel}
            value={
              embryoSex
                ? sexDisclosure.hidden
                  ? undefined
                  : embryoSex.value
                : embryoSexPlaceholder
            }
            icon={UserRound}
            muted={!embryoSex}
          >
            {embryoSex && sexDisclosure.hidden ? (
              <ShowEmbryoSexButton onPress={sexDisclosure.reveal} />
            ) : undefined}
          </ValueGridTile>
        </View>
        {gradeExplanation && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
            }}
          >
            {gradeExplanation}
          </Text>
        )}
      </DetailCard>

      {specimen.developmentObservations &&
        specimen.developmentObservations.length > 0 && (
          <DetailCard title={t("cryo_vault.modal.embryo_development_section")}>
            <View style={{ gap: 12 }}>
              {specimen.developmentObservations.map((observation, index) => {
                const grade = formatEmbryoGradeFromDayDetails([observation]);
                const stage = readEmbryoStage(observation);
                const observationDetails = compactParts([
                  stage
                    ? t("cryo_vault.modal.development_stage_value", {
                        params: {
                          stage: formatEnumValue(stage) ?? "",
                        },
                      })
                    : null,
                  grade
                    ? t("cryo_vault.modal.development_grade_value", {
                        params: { grade },
                      })
                    : null,
                  observation.cellCount != null
                    ? t("cryo_vault.modal.development_cells_value", {
                        params: { count: String(observation.cellCount) },
                      })
                    : null,
                  observation.isArrested
                    ? t("cryo_vault.modal.development_arrested_value")
                    : null,
                ]);
                const provenance = compactParts([
                  observation.source
                    ? t("cryo_vault.modal.development_record_source", {
                        params: {
                          source: formatEnumValue(observation.source) ?? "",
                        },
                      })
                    : null,
                  observation.verificationStatus
                    ? t("cryo_vault.modal.development_record_verification", {
                        params: {
                          status:
                            formatEnumValue(observation.verificationStatus) ??
                            "",
                        },
                      })
                    : null,
                ]);

                return (
                  <View
                    key={
                      observation.id ??
                      `development-day-${observation.day}-${index}`
                    }
                    style={{ flexDirection: "row", gap: 10 }}
                  >
                    <View
                      style={{
                        width: 48,
                        paddingTop: 1,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          lineHeight: 16,
                          fontWeight: "800",
                          color: EMBER.colors.text,
                        }}
                      >
                        {t("cryo_vault.modal.development_day_value", {
                          params: { day: String(observation.day) },
                        })}
                      </Text>
                    </View>
                    <View style={{ flex: 1, gap: 3 }}>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          lineHeight: 17,
                          color: EMBER.colors.text,
                        }}
                      >
                        {observationDetails ??
                          t("cryo_vault.modal.development_not_recorded")}
                      </Text>
                      {observation.notes && (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 11,
                            lineHeight: 15,
                            color: EMBER.colors.textLight,
                          }}
                        >
                          {observation.notes}
                        </Text>
                      )}
                      {provenance && (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 10,
                            lineHeight: 14,
                            color: EMBER.colors.textFaint,
                          }}
                        >
                          {provenance}
                        </Text>
                      )}
                    </View>
                  </View>
                );
              })}
            </View>
          </DetailCard>
        )}
    </>
  );

  const renderTabContent = () => {
    if (selectedTab === "clinical") {
      return (
        <>
          <DetailSection
            title={t("cryo_vault.modal.embryo_section")}
            rows={embryoRows}
          />
        </>
      );
    }

    if (selectedTab === "storage") {
      return <DetailSection rows={storageRows} />;
    }

    if (selectedTab === "usage") {
      return renderUsageContent();
    }

    if (selectedTab === "documents") {
      if (specimen.type === "sperm") {
        return (
          <DocumentRequirementList
            title={t("cryo_vault.modal.source_documents_section")}
            items={semenDocumentRequirements}
            statusLabel={t("cryo_vault.modal.source_not_linked")}
            actionLabel={t("cryo_vault.modal.upload_document")}
            onActionPress={handleDocumentActionPress}
          />
        );
      }

      if (specimen.type === "oocyte") {
        return (
          <DocumentRequirementList
            title={t("cryo_vault.modal.source_documents_section")}
            items={oocyteDocumentRequirements}
            statusLabel={t("cryo_vault.modal.source_not_linked")}
            actionLabel={t("cryo_vault.modal.upload_document")}
            onActionPress={handleDocumentActionPress}
          />
        );
      }

      return (
        <DocumentRequirementList
          title={t("cryo_vault.modal.source_documents_section")}
          items={embryoDocumentRequirements}
          statusLabel={t("cryo_vault.modal.source_not_linked")}
          actionLabel={t("cryo_vault.modal.upload_document")}
          onActionPress={handleDocumentActionPress}
        />
      );
    }

    if (specimen.type === "sperm") {
      return renderSemenSummaryContent();
    }

    if (specimen.type === "oocyte") {
      return renderOocyteSummaryContent();
    }

    return renderEmbryoSummaryContent();
  };

  const modalContent = (
    <View>
      <View
        pointerEvents="box-none"
        style={{
          position: "absolute",
          top: -18,
          left: -10,
          zIndex: 1,
        }}
      >
        {onDelete && canDeleteUserRecord && (
          <IconButton
            accessibilityLabel={t(
              "cryo_vault.modal.delete_specimen_record_action",
            )}
            onPress={() => {
              setDeleteError(null);
              setDeleteConfirmationVisible(true);
            }}
            hitSlop={12}
            action="cryo_specimen_delete_record"
            testID="delete-cryo-specimen-record-button"
            size={32}
            surface="soft"
            backgroundColor={semanticColors.action.destructive.background}
            pressedBackgroundColor={
              semanticColors.action.destructive.pressedBackground
            }
            pressedOpacity={1}
          >
            <Trash2
              size={16}
              color={semanticColors.action.destructive.foreground}
              strokeWidth={1.8}
            />
          </IconButton>
        )}
      </View>
      <SpecimenModalHeader
        image={headerImage}
        title={specimenTitle}
        tags={headerTags}
        metadataTags={cycleLabelParts}
        compactLayout={compactLayout}
      />
      <MoonSegmentedControl
        active={selectedTab}
        onChange={setActiveTab}
        tabs={tabOptions}
      />

      <ScrollView
        key={selectedTab}
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: modalMaxHeight }}
        contentContainerStyle={{ paddingBottom: 4 }}
      >
        {renderTabContent()}
      </ScrollView>

      {(primaryFooterAction || originCycleAction) && (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.subtle,
            paddingTop: 14,
            marginTop: 16,
          }}
        >
          <View style={{ flexDirection: "row", gap: 8, alignItems: "stretch" }}>
            {originCycleAction && (
              <SecondaryButton
                flex={primaryFooterAction ? 0.7 : 1}
                label={originCycleActionLabel}
                onPress={originCycleAction}
              />
            )}
            {primaryFooterAction && primaryFooterActionLabel && (
              <PrimaryButton
                flex={originCycleAction ? 1.3 : undefined}
                fullWidth={!originCycleAction}
                label={primaryFooterActionLabel}
                onPress={primaryFooterAction}
                contentStyle={
                  originCycleAction ? { paddingHorizontal: 12 } : undefined
                }
              />
            )}
          </View>
        </View>
      )}
    </View>
  );

  return (
    <>
      <Dialog
        containerStyle={{ maxHeight: dialogMaxHeight }}
        visible={!deleteConfirmationVisible}
        onClose={onClose}
        closeButton="minimal"
        surfaceStyle={{ maxHeight: dialogMaxHeight }}
        contentStyle={{
          paddingTop: 30,
          paddingHorizontal: 22,
          paddingBottom: 24,
          maxHeight: dialogMaxHeight,
        }}
      >
        {modalContent}
      </Dialog>
      <EditCryoSpecimenFieldDialog
        visible={editingField != null}
        specimen={specimen}
        field={editingField}
        onClose={() => setEditingField(null)}
      />
      <CryoSpecimenLifecycleActionPicker
        visible={lifecycleActionPickerVisible}
        specimen={specimen}
        onClose={() => setLifecycleActionPickerVisible(false)}
        onSelect={(choice) => {
          setLifecycleActionPickerVisible(false);
          setLifecycleChoice(choice);
        }}
      />
      <RecordCryoSpecimenLifecycleDialog
        visible={lifecycleChoice != null}
        specimen={specimen}
        choice={lifecycleChoice}
        onClose={() => setLifecycleChoice(null)}
      />
      <Dialog
        containerStyle={{ maxHeight: dialogMaxHeight }}
        visible={deleteConfirmationVisible}
        onClose={() => {
          if (!deletePending) setDeleteConfirmationVisible(false);
        }}
        closeButton="minimal"
        dismissOnBackdropTap={!deletePending}
        surfaceStyle={{ maxHeight: dialogMaxHeight }}
        contentStyle={{
          paddingTop: 28,
          paddingHorizontal: 24,
          paddingBottom: 24,
          maxHeight: dialogMaxHeight,
        }}
        testID="delete-cryo-specimen-dialog"
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 4 }}
        >
          <View style={{ alignItems: "center" }}>
            <Image
              testID="delete-cryo-specimen-trash-illustration"
              source={SPECIMEN_DELETE_ILLUSTRATION}
              style={{ width: 80, height: 96, marginBottom: 12 }}
              resizeMode="contain"
            />
            <DialogHeader
              align="center"
              title={t("cryo_vault.modal.confirm_delete_specimen_record")}
              subtitle={t("cryo_vault.modal.delete_specimen_record_warning", {
                params: { specimen: specimen.label },
              })}
              style={{ marginBottom: 20 }}
            />
            <View style={{ width: "100%", marginBottom: 18 }}>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.text,
                  fontSize: 13,
                  lineHeight: 18,
                  fontWeight: "600",
                  marginBottom: 8,
                }}
              >
                {t("cryo_vault.modal.record_disposition_instead_prompt")}
              </Text>
              <View style={{ gap: 8 }}>
                <SecondaryButton
                  label={t("cryo_vault.modal.record_used_instead")}
                  action="cryo_specimen_record_use_from_delete_confirmation"
                  testID="record-cryo-specimen-use-instead"
                  leading={
                    <Activity
                      size={16}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.8}
                    />
                  }
                  onPress={() => handleRecordDispositionInstead("use")}
                  disabled={deletePending}
                  style={{ paddingVertical: 10 }}
                />
                <SecondaryButton
                  label={t("cryo_vault.modal.record_discarded_instead")}
                  action="cryo_specimen_record_discard_from_delete_confirmation"
                  testID="record-cryo-specimen-discard-instead"
                  leading={
                    <Trash2
                      size={16}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.8}
                    />
                  }
                  onPress={() => handleRecordDispositionInstead("discard")}
                  disabled={deletePending}
                  style={{ paddingVertical: 10 }}
                />
                <SecondaryButton
                  label={t("cryo_vault.modal.record_donated_instead")}
                  action="cryo_specimen_record_donation_from_delete_confirmation"
                  testID="record-cryo-specimen-donation-instead"
                  leading={
                    <Microscope
                      size={16}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.8}
                    />
                  }
                  onPress={() => handleRecordDispositionInstead("donate")}
                  disabled={deletePending}
                  style={{ paddingVertical: 10 }}
                />
              </View>
            </View>
            {deleteError ? (
              <ActionBanner
                variant="important"
                message={deleteError}
                style={{ alignSelf: "stretch", marginBottom: 16 }}
              />
            ) : null}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(
                "cryo_vault.modal.confirm_delete_specimen_record_action",
              )}
              accessibilityState={{ disabled: deletePending }}
              testID="confirm-delete-cryo-specimen-record-button"
              onPress={() => {
                capturePress(
                  pressLabel(
                    t("cryo_vault.modal.confirm_delete_specimen_record_action"),
                  ),
                  "cryo_specimen_delete_record_confirm",
                );
                void handleDeleteConfirmed();
              }}
              disabled={deletePending}
              style={{
                alignSelf: "center",
                minHeight: 44,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                paddingHorizontal: 12,
              }}
            >
              {deletePending ? (
                <ActivityIndicator size="small" color={EMBER.colors.coral} />
              ) : null}
              <Text
                className="font-dm-sans"
                style={{
                  color: deletePending
                    ? EMBER.colors.textLight
                    : EMBER.colors.coral,
                  fontSize: 14,
                  fontWeight: "600",
                  lineHeight: 20,
                  textAlign: "center",
                }}
              >
                {t("cryo_vault.modal.confirm_delete_specimen_record_action")}
              </Text>
            </Pressable>
          </View>
        </ScrollView>
      </Dialog>
      <ClinicalMetricExplanationDialog
        explanation={selectedMetricExplanation}
        onClose={() => setSelectedMetricExplanation(null)}
      />
    </>
  );
}
